import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.statistics');
import { OnInit } from '@angular/core';
import { Service } from "src/libs/portal/season/service";

type ColumnKind = 'numeric' | 'categorical' | 'date' | 'empty';

interface ColumnProfile {
    name: string;
    type: ColumnKind;
    count: number;
    missing: number;
    distinct: number;
    levels: string[];
    mean?: number;
    sd?: number;
    min?: number;
    median?: number;
    max?: number;
}

interface Recommendation {
    id: string;
    label: string;
    reason: string;
    score: number;
    outcome?: string;
    predictor?: string;
    group?: string;
}

interface AnalysisResult {
    title: string;
    subtitle: string;
    metrics: any[];
    tables: any[];
    notes: string[];
    conclusion: string;
    status: 'ready' | 'warning';
    pValue?: number;
    paper?: any;
}

interface NormalityResult {
    variable: string;
    n: number;
    statistic: number;
    pValue: number;
    stars: string;
    normal: boolean;
    interpretation: string;
}

interface ParallelTestResult {
    id: string;
    model: string;
    label: string;
    outcome?: string;
    predictor?: string;
    group?: string;
    pValue: number;
    pLabel: string;
    decision: string;
    rationale: string;
}

interface GraphRecommendation {
    id: string;
    label: string;
    kind: string;
    reason: string;
    previewType: string;
    priority: string;
    insight: string;
    paperTitle: string;
    xLabel: string;
    yLabel: string;
    annotation: string;
    significant: boolean;
    pLabel: string;
    preview: any;
}

interface AnalysisNeed {
    needed: boolean;
    label: string;
    reason: string;
    recommendation: string;
    confidence: number;
}

interface ConclusionBlueprint {
    goalType: string;
    intentSummary: string;
    sourceSummary: string;
    question: string;
    comparisonAxis: string;
    recommendedAnalysis: string;
    outcomeCandidates: string[];
    predictorCandidates: string[];
    groupCandidates: string[];
    rationale: string;
}

interface DatasetBlockSummary {
    index: number;
    name: string;
    text: string;
    rowCount: number;
    columnCount: number;
    numericCount: number;
    categoricalCount: number;
    recommendation: string;
    normality: string;
    status: string;
    normalityResults: NormalityResult[];
    tests: ParallelTestResult[];
    selectedTest?: ParallelTestResult;
    manuscriptSentence: string;
}

interface AiChatMessage {
    role: 'ai' | 'user';
    text: string;
}

@Component({
    selector: 'wiz-page-statistics',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.statistics/view.scss */
:host {
  display: block;
  height: 100%;
}

.plot-preview {
  font-family: Inter, Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.plot-grid {
  stroke: #e2e8f0;
  stroke-width: 1;
  stroke-dasharray: 4 5;
}

.plot-series .boxplot-box,
.plot-series .summary-bar {
  filter: drop-shadow(0 7px 8px rgba(15, 23, 42, 0.08));
  transform-box: fill-box;
  transform-origin: center;
  transition: filter 160ms ease, transform 160ms ease;
}

.data-point {
  cursor: crosshair;
  opacity: 0.68;
  stroke: #ffffff;
  stroke-width: 1.2px;
  transform-box: fill-box;
  transform-origin: center;
  transition: opacity 140ms ease, transform 140ms ease, stroke-width 140ms ease;
}

.plot-series:hover .boxplot-box,
.plot-series:hover .summary-bar {
  filter: drop-shadow(0 10px 12px rgba(15, 23, 42, 0.14));
  transform: translateY(-1px);
}

.plot-series:hover .data-point {
  opacity: 0.9;
}

.data-point:hover {
  opacity: 1;
  stroke-width: 2px;
  transform: scale(1.45);
}

.data-outlier {
  opacity: 1;
}

.trend-band {
  stroke: #c7d2fe;
  stroke-width: 18px;
  stroke-linecap: round;
  opacity: 0.42;
}

@media (prefers-reduced-motion: reduce) {
  .plot-series .boxplot-box,
  .plot-series .summary-bar,
  .data-point {
    transition: none;
  }
}`],
})
export class PageStatisticsComponent implements OnInit {
    public step: number = 1;
    public dataContext: string = '칩 디자인 개선 전후의 purity와 recovery rate를 비교합니다. 개선 후가 더 높고, defect rate는 더 낮아야 합니다.';
    public datasetText: string = `Condition,Purity,Recovery_Rate,Defect_Rate,Cycle_Time
Before,91.2,78.4,4.8,12.5
Before,92.0,79.1,4.5,12.1
Before,90.6,77.8,5.0,12.8
Before,91.8,80.2,4.3,11.9
Before,89.9,76.5,5.4,13.0
Before,92.4,79.8,4.1,12.3
Before,90.8,78.9,4.7,12.7
Before,91.5,77.6,4.9,12.4
After,96.1,85.4,2.6,11.4
After,95.7,84.8,2.9,11.2
After,97.0,86.7,2.1,10.9
After,96.5,85.9,2.4,11.0
After,95.9,84.5,2.7,11.5
After,97.3,87.1,2.0,10.8
After,96.8,86.2,2.2,11.1
After,95.4,84.1,3.0,11.6`;

    public delimiterMode: string = 'auto';
    public hasHeader: boolean = true;
    public sheet: string[][] = [];
    public sheetRows: number[] = [];
    public sheetColumns: number[] = [];
    public activeCell: any = { row: 0, col: 0 };
    public selectionStart: any = { row: 0, col: 0 };
    public selectionEnd: any = { row: 0, col: 0 };
    public isSelectingCells: boolean = false;
    public minSheetRows: number = 24;
    public minSheetColumns: number = 10;
    public selectedAnalysis: string = 'auto';
    public activeAnalysis: string = '';
    public evidenceMode: string = 'guided';
    public analysisSubmitted: boolean = false;
    public outcome: string = '';
    public predictor: string = '';
    public group: string = '';
    public selectedOutcomes: string[] = [];
    public selectedPredictors: string[] = [];
    public selectedGroups: string[] = [];
    public positiveLevel: string = '';
    public aiChatInput: string = '';
    public aiChatMessages: AiChatMessage[] = [];
    public aiPromptSuggestions: string[] = [
        '이 배경설명에서 결론 질문을 먼저 뽑아줘',
        '왜 비교해야 하는지 설명해줘',
        '무엇끼리 어떻게 비교해야 결론이 나는지 잡아줘',
        '이 데이터로 낼 수 있는 결론 초안을 써줘'
    ];

    public rows: any[] = [];
    public columns: ColumnProfile[] = [];
    public numericColumns: ColumnProfile[] = [];
    public categoricalColumns: ColumnProfile[] = [];
    public recommendations: Recommendation[] = [];
    public graphRecommendations: GraphRecommendation[] = [];
    public selectedGraphId: string = '';
    public dataUnderstanding: string[] = [];
    public analysisNeed: AnalysisNeed = {
        needed: true,
        label: '결론 근거 필요',
        reason: '데이터 설명을 기준으로 검정 가능성을 확인합니다.',
        recommendation: '데이터 구조를 읽은 뒤 결론을 뒷받침할 비교 근거를 설계합니다.',
        confidence: 0.5
    };
    public conclusionBlueprint: ConclusionBlueprint = {
        goalType: '대기',
        intentSummary: '데이터와 연구 배경을 기다리는 중입니다.',
        sourceSummary: '아직 데이터 학습 전입니다.',
        question: '',
        comparisonAxis: '',
        recommendedAnalysis: 'auto',
        outcomeCandidates: [],
        predictorCandidates: [],
        groupCandidates: [],
        rationale: '배경설명과 대화를 읽은 뒤 결론 가닥을 먼저 정합니다.'
    };
    public datasetBlocks: DatasetBlockSummary[] = [];
    public selectedDatasetIndex: number = 0;
    public normalityResults: NormalityResult[] = [];
    public modelRationale: string[] = [];
    public manuscriptSentence: string = '';
    public alerts: string[] = [];
    public result: AnalysisResult | null = null;

    public modelOptions: any = {
        alpha: 0.05,
        includeIntercept: true,
        effectSize: true,
        standardize: false
    };

    public delimiterOptions: any[] = [
        { id: 'auto', label: '자동' },
        { id: 'comma', label: '쉼표' },
        { id: 'tab', label: '탭' },
        { id: 'semicolon', label: '세미콜론' },
        { id: 'pipe', label: '파이프' }
    ];

    public typeOptions: any[] = [
        { id: 'numeric', label: '숫자형' },
        { id: 'categorical', label: '범주형' },
        { id: 'date', label: '날짜형' }
    ];

    public alphaOptions: number[] = [0.1, 0.05, 0.01];

    public analysisCatalog: any[] = [
        { id: 'auto', label: '자동 추천' },
        { id: 'descriptive', label: '기술통계 / 빈도분석' },
        { id: 'correlation', label: '상관분석' },
        { id: 'ttest', label: '독립표본 t-test' },
        { id: 'anova', label: '일원분산분석' },
        { id: 'chisquare', label: '카이제곱 검정' },
        { id: 'linear', label: '선형회귀' },
        { id: 'logistic', label: '로지스틱 회귀' }
    ];

    public workflowSteps: any[] = [
        { id: 1, label: 'AI 대화' },
        { id: 2, label: '분석 의뢰서' },
        { id: 3, label: '분석 결과' },
        { id: 4, label: '그래프/논문' }
    ];

    private readonly missingTokens: string[] = ['', '.', 'na', 'n/a', 'null', 'none', 'nan', '-'];

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        this.rebuildSheetFromDataset();
        await this.analyze();
    }

    public async analyze() {
        this.syncDatasetFromSheet();
        this.buildDatasetBlocks();
        if (this.datasetBlocks.length > 0 && this.selectedDatasetIndex < this.datasetBlocks.length) {
            this.datasetText = this.datasetBlocks[this.selectedDatasetIndex].text;
        }
        this.parseDataset();
        this.refreshColumnGroups();
        this.ensureSelections();
        this.buildConclusionBlueprint();
        if (this.evidenceMode === 'guided') this.applyConclusionBlueprint();
        this.analysisNeed = this.evaluateAnalysisNeed();
        this.buildRecommendations();
        this.autoApplyRecommendation();
        this.buildDataUnderstanding();
        this.runSelected();
        this.ensureAiChatStarted();
        await this.service.render();
    }

    public async loadSample() {
        this.dataContext = '칩 디자인 개선 전후의 purity와 recovery rate를 비교합니다. 개선 후가 더 높고, defect rate는 더 낮아야 합니다.';
        this.datasetText = `Condition,Purity,Recovery_Rate,Defect_Rate,Cycle_Time
Before,91.2,78.4,4.8,12.5
Before,92.0,79.1,4.5,12.1
Before,90.6,77.8,5.0,12.8
Before,91.8,80.2,4.3,11.9
Before,89.9,76.5,5.4,13.0
Before,92.4,79.8,4.1,12.3
Before,90.8,78.9,4.7,12.7
Before,91.5,77.6,4.9,12.4
After,96.1,85.4,2.6,11.4
After,95.7,84.8,2.9,11.2
After,97.0,86.7,2.1,10.9
After,96.5,85.9,2.4,11.0
After,95.9,84.5,2.7,11.5
After,97.3,87.1,2.0,10.8
After,96.8,86.2,2.2,11.1
After,95.4,84.1,3.0,11.6`;
        this.delimiterMode = 'auto';
        this.hasHeader = true;
        this.selectedAnalysis = 'auto';
        this.evidenceMode = 'guided';
        this.analysisSubmitted = false;
        this.step = 1;
        this.rebuildSheetFromDataset();
        this.resetCellSelection();
        this.aiChatMessages = [];
        this.aiChatInput = '';
        await this.analyze();
    }

    public async clearData() {
        this.datasetText = '';
        this.step = 1;
        this.evidenceMode = 'guided';
        this.analysisSubmitted = false;
        this.createBlankSheet();
        this.resetCellSelection();
        this.aiChatMessages = [];
        this.aiChatInput = '';
        this.rows = [];
        this.columns = [];
        this.numericColumns = [];
        this.categoricalColumns = [];
        this.recommendations = [];
        this.graphRecommendations = [];
        this.dataUnderstanding = [];
        this.outcome = '';
        this.predictor = '';
        this.group = '';
        this.selectedOutcomes = [];
        this.selectedPredictors = [];
        this.selectedGroups = [];
        this.analysisNeed = {
            needed: true,
            label: '결론 근거 필요',
            reason: '데이터 설명을 기준으로 검정 가능성을 확인합니다.',
            recommendation: '데이터 구조를 읽은 뒤 결론을 뒷받침할 비교 근거를 설계합니다.',
            confidence: 0.5
        };
        this.conclusionBlueprint = this.emptyConclusionBlueprint();
        this.datasetBlocks = [];
        this.selectedDatasetIndex = 0;
        this.normalityResults = [];
        this.modelRationale = [];
        this.manuscriptSentence = '';
        this.alerts = [];
        this.result = null;
        this.ensureAiChatStarted();
        await this.service.render();
    }

    public async selectDatasetBlock(block: DatasetBlockSummary) {
        this.selectedDatasetIndex = block.index;
        this.datasetText = block.text;
        this.selectedAnalysis = 'auto';
        this.evidenceMode = 'guided';
        this.analysisSubmitted = false;
        await this.analyze();
    }

    public chatMessageClass(message: AiChatMessage) {
        if (message.role === 'user') return 'ml-8 bg-indigo-600 text-white';
        return 'mr-8 border border-gray-200 bg-white text-gray-700';
    }

    public updateAiChatInputFromEvent(event: Event) {
        let target = event.target as HTMLInputElement;
        this.aiChatInput = target.value;
    }

    public markConclusionContextChanged() {
        this.evidenceMode = 'guided';
        this.analysisSubmitted = false;
        if (this.rows.length > 0 && this.columns.length > 0) {
            this.buildConclusionBlueprint();
            this.applyConclusionBlueprint();
            this.analysisNeed = this.evaluateAnalysisNeed();
            this.buildRecommendations();
            this.runSelected();
            this.buildDataUnderstanding();
        }
    }

    public async sendAiChatOnEnter(event: KeyboardEvent) {
        event.preventDefault();
        await this.sendAiChat();
    }

    public aiConversationSuggestions() {
        if (this.rows.length === 0 || this.columns.length === 0) {
            return [
                '데이터를 넣으면 어떤 결론 질문을 뽑을 수 있어?',
                '통계가 필요 없는 데이터인지 판단해줘',
                '배경설명에는 무엇을 써야 해?'
            ];
        }
        if (this.isExperimentalComparisonContext()) {
            return [
                '개선 후가 정말 좋아졌다고 말해도 돼?',
                '왜 전후 비교가 필요한지 설명해줘',
                'defect는 낮아지는 게 좋은 지표야',
                '결론 초안을 더 논문스럽게 써줘'
            ];
        }
        if (['ttest', 'anova'].indexOf(this.activeAnalysis || this.selectedAnalysis) >= 0) {
            return [
                '어떤 집단끼리 비교하는 거야?',
                '이 비교로 낼 수 있는 결론을 말해줘',
                '메인 그래프는 뭐가 좋아?',
                '결론을 보류해야 하는 경우도 알려줘'
            ];
        }
        return [
            '이 데이터로 낼 수 있는 결론 질문을 뽑아줘',
            '왜 이 분석이 결론 근거가 되는지 설명해줘',
            '그래프와 결론 문장을 같이 잡아줘',
            '결론을 보류해야 하는지 판단해줘'
        ];
    }

    public async sendAiQuickReply(text: string) {
        this.aiChatInput = text;
        await this.sendAiChat();
    }

    public async sendAiChat() {
        let text = (this.aiChatInput || '').trim();
        if (!text) return;

        this.aiChatMessages.push({ role: 'user', text });
        this.aiChatInput = '';
        this.evidenceMode = 'guided';
        this.analysisSubmitted = false;

        try {
            this.dataContext = this.appendConversationContext(text);
            this.syncDatasetFromSheet();
            this.buildDatasetBlocks();
            if (this.datasetBlocks.length > 0 && this.selectedDatasetIndex < this.datasetBlocks.length) {
                this.datasetText = this.datasetBlocks[this.selectedDatasetIndex].text;
            }
            this.parseDataset();
            this.refreshColumnGroups();
            this.ensureSelections();
            this.applyConversationIntent(text);
            this.buildConclusionBlueprint();
            this.applyConclusionBlueprint();
            this.analysisNeed = this.evaluateAnalysisNeed();
            this.buildRecommendations();
            this.autoApplyRecommendation();
            this.buildDataUnderstanding();
            this.runSelected();
            this.aiChatMessages.push({ role: 'ai', text: this.composeAiChatReply(text) });
        } catch (error) {
            this.aiChatMessages.push({
                role: 'ai',
                text: '방금 메시지를 결론 설계에 반영하는 중 문제가 생겼습니다.\n입력한 문장은 대화에 남겨두었고, 데이터 표와 배경설명을 다시 읽은 뒤 결론 질문부터 재정리할게요.'
            });
        }
        await this.service.render();
    }

    public activateCell(row: number, col: number) {
        this.activeCell = { row, col };
    }

    public startCellSelection(row: number, col: number, event: MouseEvent) {
        if (event.button !== 0) return;
        this.activeCell = { row, col };
        this.selectionStart = { row, col };
        this.selectionEnd = { row, col };
        this.isSelectingCells = true;
    }

    public extendCellSelection(row: number, col: number, event: MouseEvent) {
        if (!this.isSelectingCells) return;
        this.selectionEnd = { row, col };
        event.preventDefault();
    }

    public finishCellSelection() {
        this.isSelectingCells = false;
    }

    public selectedRangeLabel() {
        let bounds = this.selectionBounds();
        let start = `${this.sheetColumnName(bounds.left)}${bounds.top + 1}`;
        let end = `${this.sheetColumnName(bounds.right)}${bounds.bottom + 1}`;
        return start === end ? start : `${start}:${end}`;
    }

    public isCellSelected(row: number, col: number) {
        let bounds = this.selectionBounds();
        return row >= bounds.top && row <= bounds.bottom && col >= bounds.left && col <= bounds.right;
    }

    public cellInputClass(row: number, col: number) {
        let active = this.activeCell.row === row && this.activeCell.col === col;
        if (active) return 'bg-indigo-100 ring-1 ring-inset ring-indigo-500';
        if (this.isCellSelected(row, col)) return 'bg-indigo-50';
        return 'bg-white hover:bg-gray-50';
    }

    public cellValue(row: number, col: number) {
        return this.sheet[row]?.[col] || '';
    }

    public updateSheetCellFromEvent(row: number, col: number, event: Event) {
        let target = event.target as HTMLInputElement;
        this.updateSheetCell(row, col, target.value);
    }

    public updateSheetCell(row: number, col: number, value: string) {
        this.ensureSheetSize(row + 1, col + 1);
        this.sheet[row][col] = value;
        this.syncDatasetFromSheet();
    }

    public sheetColumnName(index: number) {
        let name = '';
        let value = index + 1;
        while (value > 0) {
            let remainder = (value - 1) % 26;
            name = String.fromCharCode(65 + remainder) + name;
            value = Math.floor((value - 1) / 26);
        }
        return name;
    }

    public async addSheetRow() {
        this.ensureSheetSize(this.sheet.length + 5, this.sheetColumns.length);
        this.syncDatasetFromSheet();
        await this.service.render();
    }

    public async addSheetColumn() {
        this.ensureSheetSize(this.sheet.length, this.sheetColumns.length + 3);
        this.syncDatasetFromSheet();
        await this.service.render();
    }

    public async commitSheetPreview() {
        this.evidenceMode = 'guided';
        this.analysisSubmitted = false;
        await this.analyze();
    }

    public async handleSheetPaste(event: ClipboardEvent) {
        let text = event.clipboardData?.getData('text/plain') || '';
        if (!text) return;

        event.preventDefault();
        let matrix = this.parseClipboardMatrix(text);
        if (matrix.length === 0) return;

        let rowStart = this.activeCell.row || 0;
        let colStart = this.activeCell.col || 0;
        let rowCount = rowStart + matrix.length;
        let colCount = colStart + matrix.reduce((max: number, row: string[]) => Math.max(max, row.length), 0);
        this.ensureSheetSize(rowCount, colCount);

        matrix.forEach((row: string[], rowOffset: number) => {
            row.forEach((value: string, colOffset: number) => {
                this.sheet[rowStart + rowOffset][colStart + colOffset] = value;
            });
        });

        this.syncDatasetFromSheet();
        this.evidenceMode = 'guided';
        this.analysisSubmitted = false;
        await this.analyze();
    }

    public syncDatasetFromSheet() {
        if (!this.sheet || this.sheet.length === 0) {
            this.datasetText = '';
            return;
        }

        let lastRow = -1;
        let lastCol = -1;
        this.sheet.forEach((row: string[], rowIndex: number) => {
            row.forEach((value: string, colIndex: number) => {
                if ((value || '').trim() !== '') {
                    lastRow = Math.max(lastRow, rowIndex);
                    lastCol = Math.max(lastCol, colIndex);
                }
            });
        });

        if (lastRow < 0 || lastCol < 0) {
            this.datasetText = '';
            return;
        }

        this.datasetText = this.sheet
            .slice(0, lastRow + 1)
            .map((row: string[]) => row.slice(0, lastCol + 1).join('\t'))
            .join('\n');
        this.delimiterMode = 'tab';
    }

    private buildDatasetBlocks() {
        this.datasetBlocks = [];
        if (!this.sheet || this.sheet.length === 0) return;

        let blocks: string[][][] = [];
        let current: string[][] = [];
        this.sheet.forEach((row: string[]) => {
            let isEmpty = row.every((value: string) => (value || '').trim() === '');
            if (isEmpty) {
                if (current.length > 0) {
                    blocks.push(current);
                    current = [];
                }
            } else {
                current.push(row.slice());
            }
        });
        if (current.length > 0) blocks.push(current);

        blocks.forEach((block: string[][], index: number) => {
            let lastCol = -1;
            block.forEach((row: string[]) => {
                row.forEach((value: string, colIndex: number) => {
                    if ((value || '').trim() !== '') lastCol = Math.max(lastCol, colIndex);
                });
            });
            if (lastCol < 0 || block.length < 2) return;

            let trimmed = block.map((row: string[]) => row.slice(0, lastCol + 1));
            let text = trimmed.map((row: string[]) => row.join('\t')).join('\n');
            let parsed = this.parseDatasetText(text);
            let normalityResults = this.normalityResultsForParsed(parsed);
            let tests = this.parallelTestsForParsed(parsed);
            let selectedTest = tests[0];
            let recommendation = selectedTest ? `${selectedTest.model} · ${selectedTest.pLabel}` : this.recommendationForParsed(parsed);
            let normality = this.normalitySummaryFromResults(normalityResults);
            this.datasetBlocks.push({
                index: this.datasetBlocks.length,
                name: this.blockName(trimmed, index),
                text,
                rowCount: parsed.rows.length,
                columnCount: parsed.columns.length,
                numericCount: parsed.numericColumns.length,
                categoricalCount: parsed.categoricalColumns.length,
                recommendation,
                normality,
                status: parsed.rows.length > 0 ? '처리 완료' : '데이터 부족',
                normalityResults,
                tests,
                selectedTest,
                manuscriptSentence: this.manuscriptForParallelTest(selectedTest, normalityResults)
            });
        });

        if (this.selectedDatasetIndex >= this.datasetBlocks.length) this.selectedDatasetIndex = 0;
    }

    private blockName(block: string[][], index: number) {
        let first = block[0]?.find((value: string) => (value || '').trim() !== '');
        if (first && !this.hasHeader) return `데이터셋 ${index + 1}`;
        return `데이터셋 ${index + 1}${first ? ` · ${first}` : ''}`;
    }

    private recommendationForParsed(parsed: any) {
        let numeric = parsed.numericColumns || [];
        let categorical = parsed.categoricalColumns || [];
        if (numeric.length >= 1 && categorical.some((column: ColumnProfile) => column.distinct > 2)) return '일원분산분석 후보';
        if (numeric.length >= 1 && categorical.some((column: ColumnProfile) => column.distinct === 2)) return 't-test 후보';
        if (numeric.length >= 2) return '상관/회귀 후보';
        if (categorical.length >= 2) return '카이제곱 후보';
        return '기술통계 후보';
    }

    private normalitySummaryForParsed(parsed: any) {
        let normality = this.normalityResultsForParsed(parsed);
        return this.normalitySummaryFromResults(normality);
    }

    private normalityResultsForParsed(parsed: any) {
        return (parsed.numericColumns || []).map((column: ColumnProfile) => {
            let values = parsed.rows.map((row: any) => this.parseNumber(row[column.name])).filter((value: number | null) => value !== null) as number[];
            return this.normalityTest(column.name, values);
        }).filter((item: NormalityResult | null) => item !== null) as NormalityResult[];
    }

    private normalitySummaryFromResults(normality: NormalityResult[]) {
        if (normality.length === 0) return '정규성 대상 숫자형 변수 없음';
        let failed = normality.filter((item: NormalityResult) => !item.normal);
        if (failed.length === 0) return '정규성 가정 충족';
        return `${failed.length}개 변수 정규성 주의`;
    }

    private parallelTestsForParsed(parsed: any) {
        let tests: ParallelTestResult[] = [];
        let numeric = parsed.numericColumns || [];
        let categorical = parsed.categoricalColumns || [];
        let rows = parsed.rows || [];

        numeric.forEach((outcome: ColumnProfile) => {
            categorical.forEach((group: ColumnProfile) => {
                let groups = this.groupNumericValuesForRows(rows, outcome.name, group.name).filter((item: any) => item.values.length > 0);
                if (groups.length === 2 && groups[0].values.length >= 2 && groups[1].values.length >= 2) {
                    let result = this.tTestForGroups(groups[0].values, groups[1].values);
                    tests.push(this.parallelTest(`ttest-${outcome.name}-${group.name}`, 'Welch t-test', `${group.name}별 ${outcome.name}`, outcome.name, '', group.name, result.p, `${group.name}의 두 집단 평균 비교가 가능합니다.`));
                } else if (groups.length >= 3) {
                    let result = this.anovaForGroups(groups);
                    tests.push(this.parallelTest(`anova-${outcome.name}-${group.name}`, '일원분산분석', `${group.name}별 ${outcome.name}`, outcome.name, '', group.name, result.p, `${group.name}의 세 집단 이상 평균 비교가 가능합니다.`));
                }
            });
        });

        for (let i = 0; i < numeric.length; i++) {
            for (let j = i + 1; j < numeric.length; j++) {
                let pairs = this.numericPairsForRows(rows, numeric[i].name, numeric[j].name);
                if (pairs.length >= 3) {
                    let xs = pairs.map((pair: any) => pair.x);
                    let ys = pairs.map((pair: any) => pair.y);
                    let r = this.correlation(xs, ys);
                    let df = pairs.length - 2;
                    let t = r * Math.sqrt(df / Math.max(1e-12, 1 - r * r));
                    let p = this.tTwoTailP(Math.abs(t), df);
                    tests.push(this.parallelTest(`corr-${numeric[i].name}-${numeric[j].name}`, 'Pearson 상관분석', `${numeric[i].name} ↔ ${numeric[j].name}`, numeric[j].name, numeric[i].name, '', p, '두 숫자형 변수의 선형 관계를 확인합니다.'));
                }
            }
        }

        for (let i = 0; i < categorical.length; i++) {
            for (let j = i + 1; j < categorical.length; j++) {
                let result = this.chiSquareForRows(rows, categorical[i].name, categorical[j].name);
                if (result.valid) {
                    tests.push(this.parallelTest(`chi-${categorical[i].name}-${categorical[j].name}`, '카이제곱 검정', `${categorical[i].name} × ${categorical[j].name}`, categorical[i].name, categorical[j].name, '', result.p, '두 범주형 변수의 독립성을 확인합니다.'));
                }
            }
        }

        categorical.filter((column: ColumnProfile) => column.distinct === 2).forEach((outcome: ColumnProfile) => {
            numeric.forEach((predictor: ColumnProfile) => {
                let result = this.logisticForRows(rows, outcome.name, predictor.name);
                if (result.valid) {
                    tests.push(this.parallelTest(`logit-${outcome.name}-${predictor.name}`, '로지스틱 회귀', `${outcome.name} ~ ${predictor.name}`, outcome.name, predictor.name, '', result.p, '이진 결과 변수의 예측 요인을 확인합니다.'));
                }
            });
        });

        return tests.sort((a: ParallelTestResult, b: ParallelTestResult) => (isFinite(a.pValue) ? a.pValue : 1) - (isFinite(b.pValue) ? b.pValue : 1));
    }

    private parallelTest(id: string, model: string, label: string, outcome: string, predictor: string, group: string, pValue: number, rationale: string): ParallelTestResult {
        return {
            id,
            model,
            label,
            outcome,
            predictor,
            group,
            pValue,
            pLabel: this.formatPWithStars(pValue),
            decision: pValue < this.modelOptions.alpha ? '유의' : '비유의',
            rationale
        };
    }

    private manuscriptForParallelTest(test: ParallelTestResult | undefined, normality: NormalityResult[]) {
        if (!test) return '분석 가능한 비교 후보가 충분하지 않아 기술통계 중심으로 보고하였다.';
        let normal = normality.find((item: NormalityResult) => item.variable === test.outcome) || normality[0];
        let normalText = normal ? `정규성 검정 결과를 확인한 후 ` : '';
        let significance = test.pValue < this.modelOptions.alpha ? '통계적으로 유의하였다' : '통계적으로 유의하지 않았다';
        let normalDetail = normal ? `; normality ${this.formatPPaper(normal.pValue)}` : '';
        return `${normalText}${test.model}을 적용한 결과, ${test.label}는 ${significance} (${this.formatPPaper(test.pValue)}${normalDetail}).`;
    }

    private groupNumericValuesForRows(rows: any[], outcome: string, group: string) {
        let grouped: any = {};
        rows.forEach((row: any) => {
            let level = this.categoryValue(row, group);
            let value = this.parseNumber(row[outcome]);
            if (!level || value === null) return;
            if (!grouped[level]) grouped[level] = [];
            grouped[level].push(value);
        });
        return Object.keys(grouped).map((level: string) => ({ level, values: grouped[level] }));
    }

    private numericPairsForRows(rows: any[], xName: string, yName: string) {
        return rows.map((row: any) => {
            let x = this.parseNumber(row[xName]);
            let y = this.parseNumber(row[yName]);
            if (x === null || y === null) return null;
            return { x, y };
        }).filter((item: any) => item !== null);
    }

    private tTestForGroups(aValues: number[], bValues: number[]) {
        let meanA = this.mean(aValues);
        let meanB = this.mean(bValues);
        let varA = this.variance(aValues);
        let varB = this.variance(bValues);
        let se = Math.sqrt(varA / aValues.length + varB / bValues.length);
        if (se === 0) return { p: 1 };
        let t = (meanA - meanB) / se;
        let numerator = Math.pow(varA / aValues.length + varB / bValues.length, 2);
        let denominator = (Math.pow(varA / aValues.length, 2) / (aValues.length - 1)) + (Math.pow(varB / bValues.length, 2) / (bValues.length - 1));
        let df = numerator / denominator;
        return { p: this.tTwoTailP(Math.abs(t), df) };
    }

    private anovaForGroups(groups: any[]) {
        let allValues: number[] = [];
        groups.forEach((item: any) => allValues = allValues.concat(item.values));
        if (groups.length < 3 || allValues.length <= groups.length) return { p: 1 };
        let grandMean = this.mean(allValues);
        let ssBetween = groups.reduce((sum: number, item: any) => sum + item.values.length * Math.pow(this.mean(item.values) - grandMean, 2), 0);
        let ssWithin = groups.reduce((sum: number, item: any) => sum + item.values.reduce((inner: number, value: number) => inner + Math.pow(value - this.mean(item.values), 2), 0), 0);
        let dfBetween = groups.length - 1;
        let dfWithin = allValues.length - groups.length;
        let msWithin = ssWithin / dfWithin;
        let f = msWithin === 0 ? 0 : (ssBetween / dfBetween) / msWithin;
        return { p: this.fPValue(f, dfBetween, dfWithin) };
    }

    private chiSquareForRows(rows: any[], rowName: string, columnName: string) {
        let pairs = rows.map((row: any) => {
            let rowValue = this.categoryValue(row, rowName);
            let columnValue = this.categoryValue(row, columnName);
            if (!rowValue || !columnValue) return null;
            return { row: rowValue, column: columnValue };
        }).filter((item: any) => item !== null);
        let rowLevels = Array.from(new Set(pairs.map((pair: any) => pair.row)));
        let columnLevels = Array.from(new Set(pairs.map((pair: any) => pair.column)));
        if (rowLevels.length < 2 || columnLevels.length < 2) return { valid: false, p: 1 };
        let matrix = rowLevels.map((row: any) => columnLevels.map((column: any) => pairs.filter((pair: any) => pair.row === row && pair.column === column).length));
        let rowTotals = matrix.map((row: number[]) => row.reduce((sum: number, value: number) => sum + value, 0));
        let columnTotals = columnLevels.map((_: any, index: number) => matrix.reduce((sum: number, row: number[]) => sum + row[index], 0));
        let total = pairs.length;
        let chi = 0;
        matrix.forEach((row: number[], rowIndex: number) => {
            row.forEach((observed: number, columnIndex: number) => {
                let expected = rowTotals[rowIndex] * columnTotals[columnIndex] / total;
                if (expected > 0) chi += Math.pow(observed - expected, 2) / expected;
            });
        });
        let df = (rowLevels.length - 1) * (columnLevels.length - 1);
        return { valid: true, p: this.chiSquarePValue(chi, df) };
    }

    private logisticForRows(rows: any[], yName: string, xName: string) {
        let levels = Array.from(new Set(rows.map((row: any) => this.categoryValue(row, yName)).filter((value: string) => value)));
        if (levels.length !== 2) return { valid: false, p: 1 };
        let positive = levels[1];
        let pairs = rows.map((row: any) => {
            let x = this.parseNumber(row[xName]);
            let yRaw = this.categoryValue(row, yName);
            if (x === null || !yRaw) return null;
            return { x, y: yRaw === positive ? 1 : 0 };
        }).filter((item: any) => item !== null);
        if (pairs.length < 6) return { valid: false, p: 1 };
        let xs = pairs.map((pair: any) => pair.x);
        let ys = pairs.map((pair: any) => pair.y);
        let xMean = this.mean(xs);
        let xSd = this.standardDeviation(xs) || 1;
        let zs = xs.map((value: number) => (value - xMean) / xSd);
        let positiveRate = (ys.reduce((sum: number, value: number) => sum + value, 0) + 0.5) / (ys.length + 1);
        let b0 = Math.log(positiveRate / (1 - positiveRate));
        let b1 = 0;
        for (let step = 0; step < 1200; step++) {
            let g0 = 0;
            let g1 = 0;
            zs.forEach((z: number, index: number) => {
                let p = this.sigmoid(b0 + b1 * z);
                g0 += ys[index] - p;
                g1 += (ys[index] - p) * z;
            });
            b0 += 0.08 * g0 / zs.length;
            b1 += 0.08 * g1 / zs.length;
        }
        let probs = zs.map((z: number) => this.sigmoid(b0 + b1 * z));
        let info00 = 0;
        let info01 = 0;
        let info11 = 0;
        probs.forEach((p: number, index: number) => {
            let w = p * (1 - p);
            info00 += w;
            info01 += w * zs[index];
            info11 += w * zs[index] * zs[index];
        });
        let det = info00 * info11 - info01 * info01;
        let seB1 = det <= 0 ? 0 : Math.sqrt(info00 / det);
        let zScore = seB1 === 0 ? 0 : b1 / seB1;
        return { valid: true, p: 2 * (1 - this.normalCdf(Math.abs(zScore))) };
    }

    public async applyRecommendation(item: Recommendation) {
        this.evidenceMode = 'manual';
        this.analysisSubmitted = true;
        this.applyRecommendationSelection(item);
        this.runSelected();
        await this.service.render();
    }

    public async chooseRecommendation(item: Recommendation) {
        await this.applyRecommendation(item);
        this.step = 3;
        await this.service.render();
    }

    public async startRecommendation() {
        this.evidenceMode = 'guided';
        this.analysisSubmitted = false;
        await this.analyze();
        if (this.rows.length === 0 || this.columns.length === 0) {
            await this.service.modal.error('먼저 데이터 설명과 표 데이터를 입력해주세요.');
            return;
        }
        if (this.recommendations.length > 0 && this.selectedAnalysis === 'auto') {
            let item = this.recommendations[0];
            this.applyRecommendationSelection(item);
            this.runSelected();
        }
        this.step = 2;
        await this.service.render();
    }

    public async submitAnalysisOrder() {
        this.evidenceMode = 'guided';
        await this.analyze();
        if (this.rows.length === 0 || this.columns.length === 0) {
            await this.service.modal.error('먼저 AI 대화 화면에서 데이터와 연구 배경을 넣어주세요.');
            return;
        }
        this.applyConclusionBlueprint();
        this.analysisNeed = this.evaluateAnalysisNeed();
        this.buildRecommendations();
        this.autoApplyRecommendation();
        this.buildDataUnderstanding();
        this.runSelected();
        this.analysisSubmitted = true;
        this.step = 3;
        this.aiChatMessages.push({ role: 'ai', text: this.composeAnalysisSubmissionReply() });
        await this.service.render();
    }

    public async nextStep() {
        if (this.step === 1) {
            await this.startRecommendation();
            return;
        }
        if (this.step === 2) {
            await this.submitAnalysisOrder();
            return;
        }
        if (this.step === 3) {
            this.analysisSubmitted = true;
            this.runSelected();
            this.step = 4;
            await this.service.render();
            return;
        }
    }

    public async previousStep() {
        this.step = Math.max(1, this.step - 1);
        await this.service.render();
    }

    public async goStep(target: number) {
        if (target <= 1) {
            this.step = 1;
        } else if (this.rows.length > 0 && this.columns.length > 0) {
            if (target >= 3 && !this.analysisSubmitted) {
                await this.submitAnalysisOrder();
                if (target === 4 && this.analysisSubmitted) this.step = 4;
                await this.service.render();
                return;
            }
            this.step = target;
        }
        await this.service.render();
    }

    public nextStepLabel() {
        if (this.step === 1) return '분석 의뢰서 만들기';
        if (this.step === 2) return '이 의뢰서로 분석 실행';
        if (this.step === 3) return '그래프 예시 보기';
        return '완료';
    }

    public stepClass(item: any) {
        if (this.step === item.id) return 'bg-indigo-600 text-white border-indigo-600';
        if (this.step > item.id) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
        return 'bg-white text-gray-500 border-gray-200';
    }

    public recommendationClass(item: Recommendation) {
        if (this.activeAnalysis === item.id || this.selectedAnalysis === item.id) {
            return 'border-indigo-300 bg-indigo-50 shadow-sm';
        }
        return 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50';
    }

    public graphRecommendationClass(item: GraphRecommendation) {
        if (this.selectedGraphId === item.id) return 'border-indigo-300 bg-indigo-50 shadow-sm';
        return 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50';
    }

    public analysisNeedClass() {
        if (this.analysisNeed.needed) return 'border-emerald-200 bg-emerald-50 text-emerald-700';
        return 'border-amber-200 bg-amber-50 text-amber-700';
    }

    public significanceLabel() {
        if (!this.result || this.result.pValue === undefined) return '검정 없음';
        return this.result.pValue < this.modelOptions.alpha ? '유의함' : '유의하지 않음';
    }

    public significanceClass() {
        if (!this.result || this.result.pValue === undefined) return 'bg-gray-100 text-gray-600';
        return this.result.pValue < this.modelOptions.alpha ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200';
    }

    public resultPLabel() {
        if (!this.result || this.result.pValue === undefined) return '-';
        return this.formatPWithStars(this.result.pValue);
    }

    public aiDecisionSummary() {
        if (this.dataUnderstanding.length > 0) return this.dataUnderstanding[0];
        return '데이터 설명과 셀 데이터를 넣으면 AI가 연구 목적, 결과 변수, 비교 구조를 먼저 판독합니다.';
    }

    public primaryGraphLabel() {
        if (this.graphRecommendations.length > 0) return this.graphRecommendations[0].label;
        return '그래프 추천 대기';
    }

    public primaryGraphAnnotation() {
        if (this.graphRecommendations.length > 0) return this.graphRecommendations[0].annotation;
        return '분석 후 유의확률과 별표가 반영된 논문용 피겨 후보를 생성합니다.';
    }

    public optimalAnalysisLabel() {
        return this.analysisLabel(this.activeAnalysis || this.selectedAnalysis || 'auto');
    }

    public manuscriptPreview() {
        return this.manuscriptSentence || '분석 후 Methods/Results에 바로 다듬어 넣을 수 있는 투고용 문장을 생성합니다.';
    }

    public analysisOrderItems() {
        return [
            { label: '연구 의도', value: this.conclusionBlueprint.intentSummary },
            { label: '분석 질문', value: this.conclusionQuestion() },
            { label: '결과 변수', value: this.selectionSummary(this.selectedOutcomes) },
            { label: '비교/예측 변수', value: this.selectionSummary(this.selectedPredictors) },
            { label: '집단/조건', value: this.selectionSummary(this.selectedGroups) },
            { label: '추천 분석', value: this.analysisLabel(this.conclusionBlueprint.recommendedAnalysis || this.selectedAnalysis) },
            { label: '분석 근거', value: this.conclusionWhy() },
            { label: '그래프 방향', value: this.primaryGraphLabel() }
        ];
    }

    public analysisSubmittedLabel() {
        if (!this.analysisSubmitted) return '전송 전';
        if (!this.result || this.result.status === 'warning') return '검토 필요';
        return '분석 완료';
    }

    public analysisSubmittedClass() {
        if (!this.analysisSubmitted) return 'border-gray-200 bg-gray-50 text-gray-600';
        if (!this.result || this.result.status === 'warning') return 'border-amber-200 bg-amber-50 text-amber-700';
        return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    }

    public resultMetricCards() {
        return this.result ? this.result.metrics.slice(0, 4) : [];
    }

    public conclusionQuestion() {
        if (this.rows.length === 0 || this.columns.length === 0) return '데이터와 배경설명을 넣으면 AI가 먼저 결론 질문을 정리합니다.';
        if (this.conclusionBlueprint.question) return this.conclusionBlueprint.question;
        if (this.isExperimentalComparisonContext()) {
            return `${this.selectionSummary(this.selectedGroups)} 조건에 따라 ${this.selectionSummary(this.selectedOutcomes)}가 기대한 방향으로 개선되었는가?`;
        }
        if (['ttest', 'anova'].indexOf(this.activeAnalysis || this.selectedAnalysis) >= 0) {
            return `${this.selectionSummary(this.selectedGroups)} 집단에 따라 ${this.selectionSummary(this.selectedOutcomes)}가 실제로 달라지는가?`;
        }
        if ((this.activeAnalysis || this.selectedAnalysis) === 'correlation') {
            return `${this.selectionSummary(this.selectedOutcomes)}와 ${this.selectionSummary(this.selectedPredictors)} 사이에 결론으로 주장할 만한 관계가 있는가?`;
        }
        if ((this.activeAnalysis || this.selectedAnalysis) === 'linear') {
            return `${this.selectionSummary(this.selectedPredictors)}가 ${this.selectionSummary(this.selectedOutcomes)}를 설명해 결론의 근거가 되는가?`;
        }
        if ((this.activeAnalysis || this.selectedAnalysis) === 'logistic') {
            return `${this.selectionSummary(this.selectedPredictors)}가 ${this.selectionSummary(this.selectedOutcomes)}의 가능성을 바꾸는가?`;
        }
        if (!this.analysisNeed.needed) return '이 데이터는 통계검정보다 기술적 요약만으로 결론을 정리해도 되는가?';
        return '데이터 구조와 배경설명을 바탕으로 어떤 결론을 주장할 수 있는가?';
    }

    public conclusionWhy() {
        if (this.rows.length === 0 || this.columns.length === 0) return '아직 데이터 구조를 읽지 못해 결론 근거를 만들 수 없습니다.';
        if (this.conclusionBlueprint.rationale) return this.conclusionBlueprint.rationale;
        if (this.isExperimentalComparisonContext()) {
            return `배경설명에 개선 전후 또는 조건 변화가 포함되어 있으므로, 단순 평균 표시가 아니라 ${this.selectionSummary(this.selectedGroups)} 기준으로 성능 지표가 기대 방향으로 바뀌었는지 확인해야 결론을 낼 수 있습니다.`;
        }
        if (['ttest', 'anova'].indexOf(this.activeAnalysis || this.selectedAnalysis) >= 0) {
            return `결론이 집단 간 차이에 달려 있으므로, ${this.selectionSummary(this.selectedGroups)}별 ${this.selectionSummary(this.selectedOutcomes)} 차이를 확인해야 합니다.`;
        }
        if ((this.activeAnalysis || this.selectedAnalysis) === 'correlation') {
            return '결론이 변수 간 동반 변화에 달려 있으므로, 관계의 방향과 크기를 먼저 확인해야 합니다.';
        }
        if (['linear', 'logistic'].indexOf(this.activeAnalysis || this.selectedAnalysis) >= 0) {
            return '결론이 예측 또는 설명 요인에 달려 있으므로, 결과 변수가 예측 변수에 의해 얼마나 달라지는지 확인해야 합니다.';
        }
        if (!this.analysisNeed.needed) return this.analysisNeed.recommendation;
        return this.analysisNeed.reason;
    }

    public comparisonPlan() {
        if (this.rows.length === 0 || this.columns.length === 0) return '데이터를 넣으면 비교 축과 결과 지표를 정합니다.';
        if (this.conclusionBlueprint.comparisonAxis) {
            return `${this.conclusionBlueprint.comparisonAxis} 이 가닥을 먼저 고정한 뒤 ${this.analysisLabel(this.conclusionBlueprint.recommendedAnalysis)}로 검증합니다.`;
        }
        if (['ttest', 'anova'].indexOf(this.activeAnalysis || this.selectedAnalysis) >= 0) {
            return `${this.selectionSummary(this.selectedGroups)}로 관측치를 나누고, ${this.selectionSummary(this.selectedOutcomes)}의 집단별 평균과 분포를 비교합니다. 정규성 검정 후 평균 차이 검정과 막대그래프+에러바/박스차트로 결론을 확인합니다.`;
        }
        if ((this.activeAnalysis || this.selectedAnalysis) === 'correlation') {
            return `${this.selectionSummary(this.selectedOutcomes)}와 ${this.selectionSummary(this.selectedPredictors)}의 변수쌍을 만들고, 상관계수와 산점도 방향이 같은 메시지를 주는지 확인합니다.`;
        }
        if ((this.activeAnalysis || this.selectedAnalysis) === 'linear') {
            return `${this.selectionSummary(this.selectedOutcomes)}를 결과로 두고 ${this.selectionSummary(this.selectedPredictors)}가 결과를 설명하는지 회귀계수, R², 산점도 추세선으로 확인합니다.`;
        }
        if ((this.activeAnalysis || this.selectedAnalysis) === 'logistic') {
            return `${this.selectionSummary(this.selectedOutcomes)}의 양성 범주를 정하고, ${this.selectionSummary(this.selectedPredictors)} 변화에 따른 예측확률 차이를 확인합니다.`;
        }
        return `숫자형 ${this.numericColumns.length}개와 범주형 ${this.categoricalColumns.length}개를 요약해 결론 도출에 필요한 변수 후보를 정리합니다.`;
    }

    public conclusionDecisionRule() {
        let normality = this.normalityResults.length > 0 ? this.normalitySummaryFromResults(this.normalityResults) : '정규성 검정 대기';
        let pRule = this.result && this.result.pValue !== undefined ? `${this.resultPLabel()} 기준으로 ${this.significanceLabel()}` : `p < ${this.modelOptions.alpha}이면 통계적 근거 확보`;
        let direction = this.isExperimentalComparisonContext() ? `배경설명의 기대 방향도 함께 확인: ${this.desiredDirectionText()}` : '효과 방향과 그래프 패턴이 결론 문장과 일치해야 합니다.';
        return `${normality}. ${pRule}. ${direction}`;
    }

    public conclusionDraft() {
        if (!this.result) return '아직 결론 초안이 없습니다. 데이터를 넣고 AI와 결론 가닥을 정하면 초안이 생성됩니다.';
        if (this.result.status === 'warning') return this.result.conclusion;
        return `현재 결론 초안: ${this.result.conclusion}`;
    }

    public conclusionReadinessLabel() {
        if (!this.result || this.result.status === 'warning') return '결론 준비 전';
        if (this.result.pValue === undefined) return '요약 결론 가능';
        return this.result.pValue < this.modelOptions.alpha ? '결론 근거 강함' : '결론 보류';
    }

    public conclusionReadinessClass() {
        if (!this.result || this.result.status === 'warning') return 'border-gray-200 bg-gray-50 text-gray-600';
        if (this.result.pValue === undefined) return 'border-sky-200 bg-sky-50 text-sky-700';
        return this.result.pValue < this.modelOptions.alpha ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700';
    }

    public conclusionEvidenceItems() {
        return [
            { label: 'AI가 읽은 배경·대화', value: this.conclusionBlueprint.intentSummary },
            { label: '데이터 학습 결과', value: this.conclusionBlueprint.sourceSummary },
            { label: '결론 질문', value: this.conclusionQuestion() },
            { label: '왜 비교하나', value: this.conclusionWhy() },
            { label: '어떻게 비교하나', value: this.comparisonPlan() },
            { label: '추천 통계분석', value: `${this.analysisLabel(this.conclusionBlueprint.recommendedAnalysis)}: ${this.conclusionBlueprint.rationale}` },
            { label: '판정 기준', value: this.conclusionDecisionRule() }
        ];
    }

    public evidenceModeLabel() {
        return this.evidenceMode === 'guided' ? '배경·대화 우선' : '수동 조정 중';
    }

    public pDisplay(value: number) {
        return this.formatPWithStars(value);
    }

    public isOutcomeSelected(name: string) {
        return this.selectedOutcomes.indexOf(name) >= 0;
    }

    public isPredictorSelected(name: string) {
        return this.selectedPredictors.indexOf(name) >= 0;
    }

    public isGroupSelected(name: string) {
        return this.selectedGroups.indexOf(name) >= 0;
    }

    public selectionSummary(values: string[]) {
        if (!values || values.length === 0) return '선택 없음';
        if (values.length <= 3) return values.join(', ');
        return `${values.slice(0, 3).join(', ')} 외 ${values.length - 3}개`;
    }

    public async toggleOutcome(name: string) {
        this.toggleSelectionValue(this.selectedOutcomes, name);
        this.syncSinglesFromMultiSelections();
        await this.onModelChanged();
    }

    public async togglePredictor(name: string) {
        this.toggleSelectionValue(this.selectedPredictors, name);
        this.syncSinglesFromMultiSelections();
        await this.onModelChanged();
    }

    public async toggleGroup(name: string) {
        this.toggleSelectionValue(this.selectedGroups, name);
        this.syncSinglesFromMultiSelections();
        await this.onModelChanged();
    }

    public async selectGraphRecommendation(item: GraphRecommendation) {
        this.selectedGraphId = item.id;
        await this.service.render();
    }

    public async onModelChanged() {
        this.evidenceMode = 'manual';
        this.syncSinglesFromMultiSelections();
        this.syncPositiveLevel();
        this.runSelected();
        this.buildDataUnderstanding();
        await this.service.render();
    }

    public async changeColumnType(column: ColumnProfile, type: ColumnKind) {
        column.type = type;
        this.evidenceMode = 'guided';
        this.refreshColumnGroups();
        this.ensureSelections();
        this.buildConclusionBlueprint();
        this.applyConclusionBlueprint();
        this.buildRecommendations();
        this.runSelected();
        await this.service.render();
    }

    public analysisLabel(id: string) {
        let found = this.analysisCatalog.find((item: any) => item.id === id);
        return found ? found.label : id;
    }

    public columnTypeLabel(type: ColumnKind) {
        let found = this.typeOptions.find((item: any) => item.id === type);
        if (found) return found.label;
        if (type === 'empty') return '비어 있음';
        return type;
    }

    public typeBadgeClass(type: ColumnKind) {
        if (type === 'numeric') return 'bg-sky-50 text-sky-700 border-sky-200';
        if (type === 'categorical') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
        if (type === 'date') return 'bg-amber-50 text-amber-700 border-amber-200';
        return 'bg-gray-50 text-gray-500 border-gray-200';
    }

    public resultStatusClass() {
        if (!this.result) return 'border-gray-200';
        return this.result.status === 'warning' ? 'border-amber-200 bg-amber-50/40' : 'border-gray-200 bg-white';
    }

    public levelsFor(name: string) {
        let column = this.columns.find((item: ColumnProfile) => item.name === name);
        return column ? column.levels : [];
    }

    public formatCell(value: any) {
        if (value === null || value === undefined || value === '') return '-';
        return String(value);
    }

    private createBlankSheet() {
        this.sheet = Array.from({ length: this.minSheetRows }).map(() => {
            return Array.from({ length: this.minSheetColumns }).map(() => '');
        });
        this.refreshSheetDimensions();
    }

    private rebuildSheetFromDataset() {
        let text = (this.datasetText || '').trim();
        if (!text) {
            this.createBlankSheet();
            return;
        }

        let lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
        let delimiter = this.resolveDelimiter(lines[0] || '');
        let matrix = lines.map((line: string) => this.splitLine(line, delimiter));
        let rows = Math.max(this.minSheetRows, matrix.length);
        let cols = Math.max(this.minSheetColumns, matrix.reduce((max: number, row: string[]) => Math.max(max, row.length), 0));

        this.sheet = Array.from({ length: rows }).map((_: any, rowIndex: number) => {
            return Array.from({ length: cols }).map((__: any, colIndex: number) => {
                return matrix[rowIndex]?.[colIndex] || '';
            });
        });
        this.refreshSheetDimensions();
    }

    private ensureSheetSize(rowCount: number, colCount: number) {
        let rows = Math.max(this.minSheetRows, rowCount);
        let cols = Math.max(this.minSheetColumns, colCount);

        if (!this.sheet || this.sheet.length === 0) {
            this.sheet = [];
        }

        while (this.sheet.length < rows) {
            this.sheet.push([]);
        }

        this.sheet.forEach((row: string[]) => {
            while (row.length < cols) {
                row.push('');
            }
        });

        this.refreshSheetDimensions();
    }

    private refreshSheetDimensions() {
        let cols = this.sheet.reduce((max: number, row: string[]) => Math.max(max, row.length), this.minSheetColumns);
        this.sheetRows = Array.from({ length: this.sheet.length }).map((_: any, index: number) => index);
        this.sheetColumns = Array.from({ length: cols }).map((_: any, index: number) => index);
    }

    private resetCellSelection() {
        this.activeCell = { row: 0, col: 0 };
        this.selectionStart = { row: 0, col: 0 };
        this.selectionEnd = { row: 0, col: 0 };
        this.isSelectingCells = false;
    }

    private selectionBounds() {
        let start = this.selectionStart || this.activeCell || { row: 0, col: 0 };
        let end = this.selectionEnd || start;
        return {
            top: Math.min(start.row, end.row),
            bottom: Math.max(start.row, end.row),
            left: Math.min(start.col, end.col),
            right: Math.max(start.col, end.col)
        };
    }

    private parseClipboardMatrix(text: string) {
        let normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        let lines = normalized.split('\n');
        if (lines.length > 0 && lines[lines.length - 1] === '') {
            lines = lines.slice(0, -1);
        }
        return lines.map((line: string) => line.split('\t').map((value: string) => value.trim()));
    }

    private parseDataset() {
        let parsed = this.parseDatasetText(this.datasetText);
        this.alerts = parsed.alerts;
        this.rows = parsed.rows;
        this.columns = parsed.columns;
        if (this.rows.length === 0) this.alerts.push('헤더만 있고 데이터 행이 없습니다.');
    }

    private parseDatasetText(text: string) {
        let alerts: string[] = [];
        text = (text || '').trim();
        if (!text) return { rows: [], columns: [], numericColumns: [], categoricalColumns: [], alerts };

        let lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter((line: string) => line.trim().length > 0);
        if (lines.length === 0) return { rows: [], columns: [], numericColumns: [], categoricalColumns: [], alerts };

        let delimiter = this.resolveDelimiter(lines[0]);
        let matrix = lines.map((line: string) => this.splitLine(line, delimiter));
        let maxWidth = matrix.reduce((max: number, row: string[]) => Math.max(max, row.length), 0);
        if (maxWidth === 0) return { rows: [], columns: [], numericColumns: [], categoricalColumns: [], alerts };

        let headers = this.hasHeader ? matrix[0] : [];
        if (!this.hasHeader) {
            headers = Array.from({ length: maxWidth }).map((_: any, index: number) => `변수${index + 1}`);
        }
        headers = this.uniqueHeaders(headers, maxWidth);

        let body = this.hasHeader ? matrix.slice(1) : matrix;
        let rows = body.map((line: string[]) => {
            let row: any = {};
            headers.forEach((header: string, index: number) => {
                row[header] = this.cleanValue(line[index]);
            });
            return row;
        });
        let columns = headers.map((name: string) => this.profileColumnFromRows(name, rows));
        let numericColumns = columns.filter((column: ColumnProfile) => column.type === 'numeric');
        let categoricalColumns = columns.filter((column: ColumnProfile) => column.type === 'categorical');
        return { rows, columns, numericColumns, categoricalColumns, alerts };
    }

    private resolveDelimiter(firstLine: string) {
        let delimiterMap: any = {
            comma: ',',
            tab: '\t',
            semicolon: ';',
            pipe: '|'
        };
        if (this.delimiterMode !== 'auto') return delimiterMap[this.delimiterMode] || ',';

        let candidates = [',', '\t', ';', '|'];
        let best = candidates[0];
        let bestCount = -1;
        candidates.forEach((candidate: string) => {
            let count = this.splitLine(firstLine, candidate).length;
            if (count > bestCount) {
                best = candidate;
                bestCount = count;
            }
        });
        return best;
    }

    private splitLine(line: string, delimiter: string) {
        let cells: string[] = [];
        let current = '';
        let quoted = false;

        for (let i = 0; i < line.length; i++) {
            let char = line[i];
            let next = line[i + 1];
            if (char === '"') {
                if (quoted && next === '"') {
                    current += '"';
                    i++;
                } else {
                    quoted = !quoted;
                }
                continue;
            }
            if (char === delimiter && !quoted) {
                cells.push(current);
                current = '';
                continue;
            }
            current += char;
        }
        cells.push(current);
        return cells;
    }

    private uniqueHeaders(headers: string[], width: number) {
        let names: string[] = [];
        let used: any = {};
        for (let i = 0; i < width; i++) {
            let raw = this.cleanValue(headers[i]) || `변수${i + 1}`;
            let base = raw.replace(/^\uFEFF/, '').trim() || `변수${i + 1}`;
            let name = base;
            let index = 2;
            while (used[name]) {
                name = `${base}_${index}`;
                index++;
            }
            used[name] = true;
            names.push(name);
        }
        return names;
    }

    private profileColumn(name: string): ColumnProfile {
        return this.profileColumnFromRows(name, this.rows);
    }

    private profileColumnFromRows(name: string, rows: any[]): ColumnProfile {
        let values = rows.map((row: any) => row[name]);
        let nonMissing = values.filter((value: any) => !this.isMissing(value));
        let numericValues = nonMissing.map((value: any) => this.parseNumber(value)).filter((value: number | null) => value !== null) as number[];
        let dateValues = nonMissing.filter((value: any) => !isNaN(Date.parse(String(value))));
        let counts: any = {};

        nonMissing.forEach((value: any) => {
            let key = String(value).trim();
            counts[key] = (counts[key] || 0) + 1;
        });

        let levels = Object.keys(counts).sort((a: string, b: string) => counts[b] - counts[a]);
        let type: ColumnKind = 'categorical';
        if (nonMissing.length === 0) type = 'empty';
        else if (numericValues.length / nonMissing.length >= 0.85) type = 'numeric';
        else if (dateValues.length / nonMissing.length >= 0.85) type = 'date';

        let profile: ColumnProfile = {
            name,
            type,
            count: nonMissing.length,
            missing: values.length - nonMissing.length,
            distinct: levels.length,
            levels: levels.slice(0, 12)
        };

        if (type === 'numeric' && numericValues.length > 0) {
            let sorted = numericValues.slice().sort((a: number, b: number) => a - b);
            profile.mean = this.mean(numericValues);
            profile.sd = this.standardDeviation(numericValues);
            profile.min = sorted[0];
            profile.median = this.quantile(sorted, 0.5);
            profile.max = sorted[sorted.length - 1];
        }

        return profile;
    }

    private refreshColumnGroups() {
        this.numericColumns = this.columns.filter((column: ColumnProfile) => column.type === 'numeric');
        this.categoricalColumns = this.columns.filter((column: ColumnProfile) => column.type === 'categorical');
    }

    private toggleSelectionValue(list: string[], name: string) {
        let index = list.indexOf(name);
        if (index >= 0) list.splice(index, 1);
        else list.push(name);
    }

    private cleanSelection(values: string[], allowed: string[], fallback: string[] = []) {
        let seen: any = {};
        let cleaned = (values || []).filter((value: string) => {
            if (allowed.indexOf(value) < 0 || seen[value]) return false;
            seen[value] = true;
            return true;
        });
        if (cleaned.length === 0) {
            cleaned = fallback.filter((value: string) => allowed.indexOf(value) >= 0 && !seen[value]);
        }
        return cleaned;
    }

    private emptyConclusionBlueprint(): ConclusionBlueprint {
        return {
            goalType: '대기',
            intentSummary: '데이터와 연구 배경을 기다리는 중입니다.',
            sourceSummary: '아직 데이터 학습 전입니다.',
            question: '',
            comparisonAxis: '',
            recommendedAnalysis: 'auto',
            outcomeCandidates: [],
            predictorCandidates: [],
            groupCandidates: [],
            rationale: '배경설명과 대화를 읽은 뒤 결론 가닥을 먼저 정합니다.'
        };
    }

    private conclusionContextText() {
        let base = (this.dataContext || '').trim();
        let conversation = this.userConversationText(base);
        return [base, conversation].filter((text: string) => text.length > 0).join('\n');
    }

    private userConversationText(existingContext: string = '') {
        return this.aiChatMessages
            .filter((message: AiChatMessage) => message.role === 'user')
            .map((message: AiChatMessage) => (message.text || '').trim())
            .filter((text: string) => text.length > 0 && existingContext.indexOf(text) < 0)
            .join('\n');
    }

    private buildConclusionBlueprint() {
        if (this.rows.length === 0 || this.columns.length === 0) {
            this.conclusionBlueprint = this.emptyConclusionBlueprint();
            return;
        }

        let rawContext = this.conclusionContextText();
        let context = rawContext.toLowerCase();
        let mentionedNumeric = this.extractMentionedColumns(rawContext, this.numericColumns);
        let mentionedCategorical = this.extractMentionedColumns(rawContext, this.categoricalColumns);
        let numericOutcomes = mentionedNumeric.length > 0 ? mentionedNumeric : this.inferNumericTargetsFromContext();
        let groups = mentionedCategorical.length > 0 ? mentionedCategorical : this.inferGroupTargetsFromContext();
        let predictors = this.inferPredictorTargetsFromContext(numericOutcomes);
        let binaryCategorical = this.categoricalColumns.filter((column: ColumnProfile) => column.distinct === 2);
        let primaryGroup = this.categoricalColumns.find((column: ColumnProfile) => column.name === groups[0]);

        let asksDifference = /차이|비교|집단|그룹|효과|평균|전후|전 후|개선|향상|좋아|높아|낮아|before|after|compare|difference|better|increase|decrease|t-test|ttest|anova/.test(context);
        let asksRelation = /관계|상관|연관|관련|correlation|associate|association/.test(context);
        let asksPrediction = /예측|영향|회귀|요인|설명|predict|prediction|regression|linear|logistic|가능성|위험|odds/.test(context);
        let asksSummary = /기술통계|빈도|요약|분포|필요없|필요 없어|검정 필요 없|descriptive|frequency|summary/.test(context);

        let analysis = 'descriptive';
        let goalType = '데이터 요약';
        let question = `${this.selectionSummary(numericOutcomes.length > 0 ? numericOutcomes : this.columns.map((column: ColumnProfile) => column.name).slice(0, 3))}를 어떻게 요약하면 연구 배경에 맞는 결론이 되는가?`;
        let axis = `${this.rows.length}개 관측치에서 숫자형 ${this.numericColumns.length}개와 범주형 ${this.categoricalColumns.length}개를 먼저 요약합니다.`;
        let rationale = '배경설명과 대화에서 명확한 비교·관계·예측 의도가 약하므로, 데이터 구조를 먼저 요약해 결론 후보를 좁힙니다.';
        let outcomeCandidates = numericOutcomes.slice(0, 6);
        let predictorCandidates = predictors.slice(0, 6);
        let groupCandidates = groups.slice(0, 4);

        if (!asksSummary && asksDifference && this.numericColumns.length > 0 && this.categoricalColumns.length > 0) {
            analysis = primaryGroup && primaryGroup.distinct > 2 ? 'anova' : 'ttest';
            goalType = '집단 차이 검증';
            outcomeCandidates = numericOutcomes.slice(0, 6);
            groupCandidates = groups.slice(0, 4);
            predictorCandidates = groupCandidates.slice();
            question = `${this.selectionSummary(groupCandidates)} 조건에 따라 ${this.selectionSummary(outcomeCandidates)}가 배경설명에서 기대한 방향으로 달라지는가?`;
            axis = `${this.selectionSummary(groupCandidates)}를 비교축으로 두고, ${this.selectionSummary(outcomeCandidates)}의 평균·분포·정규성을 먼저 확인합니다.`;
            rationale = `배경설명/대화가 조건 변화나 개선 여부를 묻고 있으므로, 데이터 구조를 학습한 뒤 ${this.selectionSummary(groupCandidates)}별 ${this.selectionSummary(outcomeCandidates)} 차이를 검증하는 모델을 우선 선택했습니다.`;
        } else if (!asksSummary && asksPrediction && (this.numericColumns.length >= 1 || binaryCategorical.length >= 1)) {
            let binaryOutcome = mentionedCategorical.find((name: string) => binaryCategorical.some((column: ColumnProfile) => column.name === name)) || (/로지스틱|logistic|가능성|위험|odds|성공|실패/.test(context) ? binaryCategorical[0]?.name : '');
            if (binaryOutcome) {
                analysis = 'logistic';
                goalType = '가능성 예측';
                outcomeCandidates = [binaryOutcome];
                predictorCandidates = this.inferPredictorTargetsFromContext(outcomeCandidates).slice(0, 6);
                question = `${this.selectionSummary(predictorCandidates)}가 ${binaryOutcome}의 가능성을 바꾸는가?`;
                axis = `${binaryOutcome}을 결과 범주로 두고, ${this.selectionSummary(predictorCandidates)} 변화에 따른 예측확률 차이를 봅니다.`;
                rationale = '배경설명/대화가 결과 발생 가능성이나 위험 요인을 묻고 있어 로지스틱 회귀를 우선 선택했습니다.';
            } else {
                analysis = 'linear';
                goalType = '설명·예측';
                outcomeCandidates = numericOutcomes.slice(0, 1);
                predictorCandidates = this.inferPredictorTargetsFromContext(outcomeCandidates).slice(0, 6);
                question = `${this.selectionSummary(predictorCandidates)}가 ${this.selectionSummary(outcomeCandidates)}를 설명해 결론의 근거가 되는가?`;
                axis = `${this.selectionSummary(outcomeCandidates)}를 결과로 두고, ${this.selectionSummary(predictorCandidates)}의 설명력을 회귀계수와 그래프로 확인합니다.`;
                rationale = '배경설명/대화가 어떤 요인이 결과를 설명하거나 예측하는지 묻고 있어 회귀 모델을 우선 선택했습니다.';
            }
        } else if (!asksSummary && asksRelation && this.numericColumns.length >= 2) {
            analysis = 'correlation';
            goalType = '관계 확인';
            outcomeCandidates = numericOutcomes.slice(0, 3);
            predictorCandidates = this.inferPredictorTargetsFromContext(outcomeCandidates).slice(0, 6);
            question = `${this.selectionSummary(outcomeCandidates)}와 ${this.selectionSummary(predictorCandidates)} 사이에 결론으로 주장할 만한 관계가 있는가?`;
            axis = `${this.selectionSummary(outcomeCandidates)}와 ${this.selectionSummary(predictorCandidates)}의 변수쌍을 만들고 상관 방향과 산점도 패턴을 확인합니다.`;
            rationale = '배경설명/대화가 변수 간 동반 변화나 연관성을 묻고 있어 상관분석을 우선 선택했습니다.';
        } else if (!asksSummary && this.isExperimentalComparisonContext() && this.numericColumns.length > 0 && this.categoricalColumns.length > 0) {
            analysis = primaryGroup && primaryGroup.distinct > 2 ? 'anova' : 'ttest';
            goalType = '개선 효과 검증';
            outcomeCandidates = numericOutcomes.slice(0, 6);
            groupCandidates = groups.slice(0, 4);
            predictorCandidates = groupCandidates.slice();
            question = `${this.selectionSummary(groupCandidates)} 조건에 따라 ${this.selectionSummary(outcomeCandidates)}가 개선 방향으로 바뀌었는가?`;
            axis = `${this.selectionSummary(groupCandidates)} 전후/조건을 기준으로 ${this.selectionSummary(outcomeCandidates)}의 변화 방향과 유의확률을 확인합니다.`;
            rationale = '배경설명에 개선 전후 또는 조건 변화가 포함되어 있어, 통계모델보다 먼저 개선 결론을 검증하는 비교 가닥을 잡았습니다.';
        } else if (!asksSummary && this.numericColumns.length > 0 && this.categoricalColumns.length > 0) {
            analysis = primaryGroup && primaryGroup.distinct > 2 ? 'anova' : 'ttest';
            goalType = '집단 비교 후보';
            outcomeCandidates = numericOutcomes.slice(0, 4);
            groupCandidates = groups.slice(0, 2);
            predictorCandidates = groupCandidates.slice();
            question = `${this.selectionSummary(groupCandidates)}에 따라 ${this.selectionSummary(outcomeCandidates)}가 달라지는지 먼저 확인할 수 있는가?`;
            axis = `데이터 구조에서 ${this.selectionSummary(groupCandidates)}와 ${this.selectionSummary(outcomeCandidates)} 조합이 가장 먼저 검증 가능한 비교축입니다.`;
            rationale = '대화 의도가 아직 넓지만 데이터에는 범주형 비교축과 숫자형 결과 지표가 있어, 집단 비교를 결론 후보로 먼저 제안합니다.';
        } else if (!asksSummary && this.numericColumns.length >= 2) {
            analysis = 'correlation';
            goalType = '관계 후보';
            outcomeCandidates = numericOutcomes.slice(0, 2);
            predictorCandidates = this.inferPredictorTargetsFromContext(outcomeCandidates).slice(0, 4);
            question = `${this.selectionSummary(outcomeCandidates)}와 ${this.selectionSummary(predictorCandidates)}가 함께 움직이는가?`;
            axis = '데이터 구조에서 숫자형 변수가 여러 개라 관계 확인을 먼저 결론 후보로 둘 수 있습니다.';
            rationale = '대화 의도가 아직 넓고 집단 비교축이 약해, 숫자형 변수 간 관계를 결론 후보로 먼저 제안합니다.';
        }

        let sources = [];
        if ((this.dataContext || '').trim()) sources.push('연구 배경');
        if (this.userConversationText((this.dataContext || '').trim()).trim()) sources.push('AI 대화');
        sources.push(`${this.rows.length}행 ${this.columns.length}변수 데이터`);

        this.conclusionBlueprint = {
            goalType,
            intentSummary: `${sources.join(' + ')}를 함께 읽어 “${goalType}” 문제로 해석했습니다.`,
            sourceSummary: `데이터에서 결과 후보 ${this.selectionSummary(outcomeCandidates)}${groupCandidates.length > 0 ? `, 비교축 ${this.selectionSummary(groupCandidates)}` : ''}${predictorCandidates.length > 0 ? `, 예측/관계 후보 ${this.selectionSummary(predictorCandidates)}` : ''}를 찾았습니다.`,
            question,
            comparisonAxis: axis,
            recommendedAnalysis: analysis,
            outcomeCandidates,
            predictorCandidates,
            groupCandidates,
            rationale
        };
    }

    private applyConclusionBlueprint() {
        if (!this.conclusionBlueprint || this.rows.length === 0 || this.columns.length === 0) return;
        let plan = this.conclusionBlueprint;
        let allNames = this.columns.map((column: ColumnProfile) => column.name);
        let numericNames = this.numericColumns.map((column: ColumnProfile) => column.name);
        let categoricalNames = this.categoricalColumns.map((column: ColumnProfile) => column.name);

        this.selectedAnalysis = plan.recommendedAnalysis || 'auto';

        if (['ttest', 'anova', 'correlation', 'linear'].indexOf(plan.recommendedAnalysis) >= 0) {
            this.selectedOutcomes = this.cleanSelection(plan.outcomeCandidates, numericNames, this.selectedOutcomes);
        } else {
            this.selectedOutcomes = this.cleanSelection(plan.outcomeCandidates, allNames, this.selectedOutcomes);
        }

        if (['ttest', 'anova'].indexOf(plan.recommendedAnalysis) >= 0) {
            this.selectedGroups = this.cleanSelection(plan.groupCandidates, categoricalNames, this.selectedGroups);
            this.selectedPredictors = this.selectedGroups.slice();
        } else {
            this.selectedPredictors = this.cleanSelection(plan.predictorCandidates, allNames, this.selectedPredictors);
            this.selectedGroups = this.cleanSelection(plan.groupCandidates, categoricalNames, this.selectedGroups);
        }

        this.syncSinglesFromMultiSelections();
        this.syncPositiveLevel();
    }

    private ensureSelections() {
        let allNames = this.columns.map((column: ColumnProfile) => column.name);
        let numericNames = this.numericColumns.map((column: ColumnProfile) => column.name);
        let categoricalNames = this.categoricalColumns.map((column: ColumnProfile) => column.name);

        if (!this.hasColumn(this.outcome)) {
            this.outcome = this.numericColumns[0]?.name || this.categoricalColumns[0]?.name || '';
        }
        if (!this.hasColumn(this.predictor)) {
            this.predictor = this.numericColumns.find((column: ColumnProfile) => column.name !== this.outcome)?.name || this.categoricalColumns.find((column: ColumnProfile) => column.name !== this.outcome)?.name || '';
        }
        if (!this.hasColumn(this.group)) {
            this.group = this.categoricalColumns[0]?.name || '';
        }
        this.selectedOutcomes = this.cleanSelection(this.selectedOutcomes, allNames, this.outcome ? [this.outcome] : numericNames.slice(0, 1));
        this.selectedPredictors = this.cleanSelection(this.selectedPredictors, allNames, this.predictor ? [this.predictor] : numericNames.filter((name: string) => name !== this.outcome).slice(0, 1));
        this.selectedGroups = this.cleanSelection(this.selectedGroups, categoricalNames, this.group ? [this.group] : categoricalNames.slice(0, 1));
        this.syncSinglesFromMultiSelections();
        this.syncPositiveLevel();
    }

    private syncSinglesFromMultiSelections() {
        let allNames = this.columns.map((column: ColumnProfile) => column.name);
        let categoricalNames = this.categoricalColumns.map((column: ColumnProfile) => column.name);
        this.selectedOutcomes = this.cleanSelection(this.selectedOutcomes, allNames, this.outcome ? [this.outcome] : []);
        this.selectedPredictors = this.cleanSelection(this.selectedPredictors, allNames, this.predictor ? [this.predictor] : []);
        this.selectedGroups = this.cleanSelection(this.selectedGroups, categoricalNames, this.group ? [this.group] : []);
        this.outcome = this.selectedOutcomes[0] || this.outcome || '';
        this.predictor = this.selectedPredictors[0] || this.predictor || '';
        this.group = this.selectedGroups[0] || this.group || '';
    }

    private syncPositiveLevel() {
        let levels = this.levelsFor(this.outcome);
        if (levels.length === 2 && levels.indexOf(this.positiveLevel) < 0) {
            this.positiveLevel = levels[1];
        }
        if (levels.length !== 2) this.positiveLevel = '';
    }

    private evaluateAnalysisNeed(): AnalysisNeed {
        let context = this.conclusionContextText().toLowerCase();
        let wantsInference = /차이|비교|집단|그룹|효과|전후|전 후|개선|향상|purity|recovery|yield|수율|순도|회수율|유의|p값|p-value|검정|가설|상관|관계|연관|관련|예측|영향|회귀|요인|모델|분산|anova|t-test|ttest|카이|chi|로지스틱|odds|위험|원인/.test(context);
        let wantsSummaryOnly = /정리|목록|리스트|명단|대장|재고|현황|기록|로그|주소|전화|이메일|고객명|제품명|코드|id|식별|관리|품질|정제|결측|중복|오류|이상치|업로드|원본|단순|합계|요약만|평균만|그래프만|시각화만|분류만/.test(context);
        let enoughForInference = this.rows.length >= 3 && (this.numericColumns.length >= 2 || (this.numericColumns.length >= 1 && this.categoricalColumns.length >= 1) || this.categoricalColumns.length >= 2);

        if (wantsInference && enoughForInference) {
            return {
                needed: true,
                label: '결론 근거 필요',
                reason: '설명에 비교, 관계, 예측, 유의성 확인 의도가 있어 결론을 주장하려면 근거 검증이 필요합니다.',
                recommendation: 'AI가 먼저 결론 질문을 세우고, 그 결론에 필요한 비교 방법과 증거 모델을 설계합니다.',
                confidence: 0.86
            };
        }

        if (wantsInference && !enoughForInference) {
            return {
                needed: false,
                label: '결론 보류',
                reason: '설명상 결론을 내고 싶은 의도는 있지만 현재 데이터 구조나 표본 수가 근거 검증을 하기에는 부족합니다.',
                recommendation: '우선 데이터 입력을 보강하거나 요약 그래프로 구조를 확인한 뒤 결론 질문을 다시 세우세요.',
                confidence: 0.78
            };
        }

        if (wantsSummaryOnly) {
            return {
                needed: false,
                label: '요약 결론',
                reason: '설명이 목록 정리, 현황 파악, 품질 확인, 단순 요약에 가까워 유의확률보다 데이터 상태 결론이 핵심입니다.',
                recommendation: '변수 요약, 결측/중복 확인, 분포 그래프를 통해 관리용 결론을 내는 것이 적합합니다.',
                confidence: 0.82
            };
        }

        if (!enoughForInference) {
            return {
                needed: false,
                label: '결론 불가',
                reason: '현재 데이터는 표본 수나 변수 조합이 부족해 결론 도출보다 데이터 확인이 먼저입니다.',
                recommendation: '행과 변수를 더 넣은 뒤 비교/예측/관계 목적을 적으면 결론 근거를 다시 설계합니다.',
                confidence: 0.74
            };
        }

        return {
            needed: true,
            label: '결론 후보 있음',
            reason: '데이터 구조상 비교, 관계, 예측 근거를 만들 수 있으므로 결론 후보를 제안합니다.',
            recommendation: '목적이 단순 정리라면 요약 결론만 사용해도 됩니다.',
            confidence: 0.62
        };
    }

    private isExperimentalComparisonContext() {
        let context = this.conclusionContextText().toLowerCase();
        return /개선|전후|전 후|before|after|이전|이후|처리|실험|공정|디자인|design|purity|recovery|yield|수율|순도|회수율/.test(context)
            && /비교|차이|효과|좋|높|낮|향상|개선|better|increase|decrease/.test(context);
    }

    private desiredDirectionText() {
        let context = this.conclusionContextText().toLowerCase();
        if (/낮아|낮게|감소|줄|decrease|lower/.test(context)) return '개선 후 값이 낮아지는 방향이 좋은 결과로 해석됩니다.';
        if (/좋|높|향상|증가|올라|increase|higher|better/.test(context)) return '개선 후 값이 높아지는 방향이 좋은 결과로 해석됩니다.';
        return '개선 후 방향성은 데이터 설명에 맞춰 해석합니다.';
    }

    private inferNumericTargetsFromContext() {
        let context = this.conclusionContextText().toLowerCase();
        let scored = this.numericColumns.map((column: ColumnProfile) => {
            let name = column.name.toLowerCase();
            let score = 0;
            if (context.indexOf(name) >= 0) score += 4;
            if (/purity|순도/.test(context) && /purity|순도/.test(name)) score += 5;
            if (/recovery|회수|회복/.test(context) && /recovery|회수|rate|율/.test(name)) score += 5;
            if (/yield|수율/.test(context) && /yield|수율/.test(name)) score += 5;
            if (/rate|ratio|percent|%|율|비율/.test(name)) score += 1;
            if (/id|번호|코드|index/.test(name)) score -= 5;
            return { name: column.name, score };
        }).sort((a: any, b: any) => b.score - a.score);
        let matched = scored.filter((item: any) => item.score > 0).map((item: any) => item.name);
        if (matched.length > 0) return matched;
        if (this.isExperimentalComparisonContext()) return this.numericColumns.map((column: ColumnProfile) => column.name).filter((name: string) => !/id|번호|코드|index/i.test(name));
        return this.numericColumns.slice(0, 2).map((column: ColumnProfile) => column.name);
    }

    private inferGroupTargetsFromContext() {
        let context = this.conclusionContextText().toLowerCase();
        let scored = this.categoricalColumns.map((column: ColumnProfile) => {
            let name = column.name.toLowerCase();
            let levels = column.levels.join(' ').toLowerCase();
            let score = 0;
            if (context.indexOf(name) >= 0) score += 3;
            if (/전|후|before|after|개선|이전|이후/.test(levels)) score += 6;
            if (/group|그룹|집단|조건|condition|처리|실험|디자인|design|version|버전|시점|time|phase|단계/.test(name)) score += 3;
            if (column.distinct === 2) score += 2;
            return { name: column.name, score };
        }).sort((a: any, b: any) => b.score - a.score);
        let matched = scored.filter((item: any) => item.score > 0).map((item: any) => item.name);
        if (matched.length > 0) return matched.slice(0, 2);
        return this.categoricalColumns.slice(0, 1).map((column: ColumnProfile) => column.name);
    }

    private inferPredictorTargetsFromContext(outcomes: string[] = []) {
        let outcomeSet: any = {};
        outcomes.forEach((name: string) => outcomeSet[name] = true);
        let numeric = this.numericColumns.map((column: ColumnProfile) => column.name).filter((name: string) => !outcomeSet[name] && !/id|번호|코드|index/i.test(name));
        if (numeric.length > 0) return numeric.slice(0, 4);
        return this.columns.map((column: ColumnProfile) => column.name).filter((name: string) => !outcomeSet[name]).slice(0, 4);
    }

    private ensureAiChatStarted() {
        if (this.aiChatMessages.length > 0) return;
        this.aiChatMessages.push({
            role: 'ai',
            text: this.composeOpeningAiMessage()
        });
    }

    private composeOpeningAiMessage() {
        if (this.rows.length === 0 || this.columns.length === 0) {
            return [
                '무엇을 밝히고 싶은지 먼저 말해주세요.',
                '데이터는 아래 셀에 엑셀에서 그대로 붙여넣고, 여기서는 연구 배경과 원하는 결론 방향을 대화로 잡으면 됩니다.',
                '제가 먼저 분석 의뢰서를 만들고, 사용자가 전송하면 통계분석과 그래프 예시까지 실행하겠습니다.'
            ].join('\n');
        }
        return [
            `데이터 학습 완료: ${this.rows.length}행, ${this.columns.length}개 변수를 읽었습니다.`,
            `배경/대화 해석: ${this.conclusionBlueprint.intentSummary}`,
            `먼저 잡은 결론 질문: ${this.conclusionQuestion()}`,
            `추천 통계분석: ${this.analysisLabel(this.conclusionBlueprint.recommendedAnalysis)}`,
            '이 방향이 맞으면 분석 의뢰서를 전송하세요. 틀리면 대화로 “결과 변수는 ...”, “...는 낮아져야 함”처럼 고쳐주면 됩니다.'
        ].join('\n');
    }

    private appendConversationContext(text: string) {
        let current = (this.dataContext || '').trim();
        if (current.indexOf(text) >= 0) return current;
        let note = `AI 대화 메모: ${text}`;
        return current ? `${current}\n${note}` : note;
    }

    private applyConversationIntent(text: string) {
        let lower = text.toLowerCase();
        let mentioned = this.extractMentionedColumns(text, this.columns);
        let mentionedNumeric = this.extractMentionedColumns(text, this.numericColumns);
        let mentionedCategorical = this.extractMentionedColumns(text, this.categoricalColumns);
        let asksDifference = /전후|전 후|비교|차이|효과|개선|향상|처리|집단|그룹|before|after|compare|difference|t-test|ttest|anova/.test(lower);
        let asksRelation = /상관|관계|연관|관련|correlation|associate/.test(lower);
        let asksPrediction = /예측|영향|회귀|요인|설명|predict|regression|linear|logistic/.test(lower);
        let asksDescriptive = /기술통계|빈도|요약|분포|필요없|필요 없어|descriptive|frequency/.test(lower);

        if (/결과|종속|반응|outcome|response|dependent|y변수|y variable/.test(lower) && mentioned.length > 0) {
            this.selectedOutcomes = mentioned.filter((name: string) => this.numericColumns.some((column: ColumnProfile) => column.name === name) || this.categoricalColumns.some((column: ColumnProfile) => column.name === name)).slice(0, 6);
        }
        if (/예측|독립|설명|영향|predictor|independent|x변수|x variable/.test(lower) && mentioned.length > 0) {
            this.selectedPredictors = mentioned.filter((name: string) => this.selectedOutcomes.indexOf(name) < 0).slice(0, 6);
        }
        if (/집단|그룹|조건|비교|전후|처리|condition|group/.test(lower) && mentionedCategorical.length > 0) {
            this.selectedGroups = mentionedCategorical.slice(0, 4);
            this.selectedPredictors = this.selectedGroups.slice();
        }

        if (asksDifference && this.numericColumns.length > 0 && this.categoricalColumns.length > 0) {
            let groups = mentionedCategorical.length > 0 ? mentionedCategorical : this.inferGroupTargetsFromContext();
            let primaryGroup = this.categoricalColumns.find((column: ColumnProfile) => column.name === groups[0]);
            this.selectedAnalysis = primaryGroup && primaryGroup.distinct > 2 ? 'anova' : 'ttest';
            this.selectedOutcomes = mentionedNumeric.length > 0 ? mentionedNumeric : this.inferNumericTargetsFromContext();
            this.selectedGroups = groups;
            this.selectedPredictors = groups.slice();
        } else if (asksRelation && this.numericColumns.length >= 2) {
            this.selectedAnalysis = 'correlation';
            this.selectedOutcomes = mentionedNumeric.length > 0 ? mentionedNumeric : this.inferNumericTargetsFromContext();
            this.selectedPredictors = this.inferPredictorTargetsFromContext(this.selectedOutcomes);
        } else if (asksPrediction) {
            let binaryOutcome = mentioned.find((name: string) => this.categoricalColumns.some((column: ColumnProfile) => column.name === name && column.distinct === 2));
            this.selectedAnalysis = /로지스틱|logistic/.test(lower) || binaryOutcome ? 'logistic' : 'linear';
            if (binaryOutcome) this.selectedOutcomes = [binaryOutcome];
            else this.selectedOutcomes = mentionedNumeric.length > 0 ? mentionedNumeric.slice(0, 1) : this.inferNumericTargetsFromContext().slice(0, 1);
            this.selectedPredictors = this.inferPredictorTargetsFromContext(this.selectedOutcomes);
        } else if (asksDescriptive) {
            this.selectedAnalysis = 'descriptive';
            if (mentioned.length > 0) this.selectedOutcomes = mentioned;
        }

        this.syncSinglesFromMultiSelections();
        this.syncPositiveLevel();
    }

    private extractMentionedColumns(text: string, columns: ColumnProfile[]) {
        let normalizedText = this.normalizeForMention(text);
        let tokens = (text || '')
            .toLowerCase()
            .split(/[^a-z0-9가-힣]+/g)
            .map((token: string) => this.normalizeForMention(token))
            .filter((token: string) => token.length >= 3);
        return columns
            .filter((column: ColumnProfile) => {
                let normalizedName = this.normalizeForMention(column.name);
                return normalizedName.length > 0 && (normalizedText.indexOf(normalizedName) >= 0 || tokens.some((token: string) => normalizedName.indexOf(token) >= 0));
            })
            .map((column: ColumnProfile) => column.name);
    }

    private normalizeForMention(value: string) {
        return (value || '').toLowerCase().replace(/[^a-z0-9가-힣]/g, '');
    }

    private composeAiChatReply(userText: string) {
        if (this.rows.length === 0 || this.columns.length === 0) {
            return [
                '좋아요. 아직 분석할 데이터 행이 부족해서 먼저 표 데이터가 필요합니다.',
                '아래 셀에 엑셀 데이터를 붙여넣고, 대화창에는 “무엇을 비교하고 싶은지”와 “어떤 방향이면 좋은지”를 계속 말해주세요.',
                '데이터를 학습한 뒤 제가 분석 의뢰서를 먼저 만들고, 사용자가 전송하면 통계분석과 그래프 예시를 실행하겠습니다.'
            ].join('\n');
        }

        let reply: string[] = [
            `반영: “${userText}”를 배경/대화 우선 결론 가닥에 넣었습니다.`,
            `데이터 학습: ${this.conclusionBlueprint.sourceSummary}`,
            `결론 질문: ${this.conclusionQuestion()}`,
            `왜 비교하나: ${this.conclusionWhy()}`,
            `어떻게 비교하나: ${this.comparisonPlan()}`,
            `판정 기준: ${this.conclusionDecisionRule()}`
        ];
        if (this.graphRecommendations.length > 0) {
            reply.push(`메인 그래프: ${this.graphRecommendations[0].label}을 우선 추천합니다. p값과 별표 표기를 같이 얹어 결론 근거가 바로 보이게 합니다.`);
        }
        if (this.result) {
            reply.push(`현재 결론 초안: ${this.result.conclusion}`);
        }
        reply.push(`다음 질문: ${this.nextAiClarifyingQuestion(userText)}`);
        return reply.join('\n');
    }

    private composeAnalysisSubmissionReply() {
        return [
            '분석 의뢰서를 전송했습니다.',
            `실행 분석: ${this.analysisLabel(this.activeAnalysis || this.selectedAnalysis)}`,
            `결과 변수: ${this.selectionSummary(this.selectedOutcomes)}`,
            `비교/예측 변수: ${this.selectionSummary(this.selectedPredictors)}`,
            `현재 판정: ${this.conclusionReadinessLabel()} (${this.resultPLabel()})`,
            `그래프 예시: ${this.primaryGraphLabel()}`
        ].join('\n');
    }

    private nextAiClarifyingQuestion(userText: string) {
        if (this.selectedOutcomes.length === 0 && this.columns.length > 0) return '가장 중요한 결과 변수가 무엇인지 변수명으로 말해주면 다음 설정을 더 좁힐 수 있어요.';
        if (['ttest', 'anova'].indexOf(this.activeAnalysis || this.selectedAnalysis) >= 0 && this.selectedGroups.length === 0) return '집단이나 조건을 나타내는 변수가 무엇인지 알려주면 비교 모델을 확정하겠습니다.';
        if (/그래프|figure|plot/.test(userText.toLowerCase())) return '이 그래프를 메인 피겨로 둘지, 박스차트를 보조 피겨로 둘지 말해주면 추천 순서를 바꿔드릴게요.';
        return '원하는 최종 결론 문장이 있으면 “결론은 개선 후가 좋아졌다는 방향이야”처럼 말해주세요. 그 결론을 검증하는 비교로 다시 맞추겠습니다.';
    }

    private buildDataUnderstanding() {
        this.dataUnderstanding = [];
        if (this.columns.length === 0 || this.rows.length === 0) return;

        let context = this.conclusionContextText();
        let goal = this.analysisNeed.reason;
        if (this.analysisNeed.needed) {
            goal = '데이터 설명을 기준으로 탐색 목적을 해석합니다.';
            if (this.isExperimentalComparisonContext()) goal = '개선 전/후 또는 조건 변화가 성능 지표를 실제로 개선했는지 확인하려는 실험 비교 데이터로 보입니다.';
            else if (/차이|비교|집단|그룹|전후|효과/.test(context)) goal = '집단 간 차이 또는 처치 효과를 확인하려는 데이터로 보입니다.';
            else if (/예측|영향|회귀|요인|설명/.test(context)) goal = '결과 변수에 영향을 주는 요인을 예측·설명하려는 데이터로 보입니다.';
            else if (/관계|상관|연관|관련/.test(context)) goal = '변수 간 관계나 연관성을 확인하려는 데이터로 보입니다.';
            else if (/빈도|비율|분포|교차/.test(context)) goal = '범주 분포나 비율 차이를 확인하려는 데이터로 보입니다.';
        }

        this.dataUnderstanding.push(goal);
        this.dataUnderstanding.push(`AI 결론 가닥: ${this.conclusionBlueprint.intentSummary}`);
        this.dataUnderstanding.push(`${this.rows.length}개 관측치와 ${this.columns.length}개 변수를 읽었고, 숫자형 ${this.numericColumns.length}개와 범주형 ${this.categoricalColumns.length}개로 분류했습니다.`);
        if (this.datasetBlocks.length > 1) this.dataUnderstanding.push(`전체 셀 범위에서 ${this.datasetBlocks.length}개 데이터셋 블록을 감지했고, 현재 ${this.datasetBlocks[this.selectedDatasetIndex]?.name || '선택 데이터셋'}을 분석 중입니다.`);
        if (this.isExperimentalComparisonContext()) {
            this.dataUnderstanding.push(`AI는 ${this.selectionSummary(this.selectedGroups)}을 비교 기준으로, ${this.selectionSummary(this.selectedOutcomes)}을 주요 성능 지표로 우선 설정했습니다.`);
            this.dataUnderstanding.push(this.desiredDirectionText());
        }
        if (!this.analysisNeed.needed) {
            this.dataUnderstanding.push(this.analysisNeed.recommendation);
            let fixedGraph = this.fixedGraphSummaryForCurrentData();
            if (fixedGraph) this.dataUnderstanding.push(fixedGraph);
            return;
        }
        if (this.selectedOutcomes.length > 0) this.dataUnderstanding.push(`현재 결과 변수 후보는 ${this.selectionSummary(this.selectedOutcomes)}입니다.`);
        if (this.selectedGroups.length > 0) this.dataUnderstanding.push(`집단/범주 비교 후보는 ${this.selectionSummary(this.selectedGroups)}입니다.`);
        let relationship = this.relationshipInsightForCurrentData();
        if (relationship) this.dataUnderstanding.push(relationship);
        let fixedGraph = this.fixedGraphSummaryForCurrentData();
        if (fixedGraph) this.dataUnderstanding.push(fixedGraph);
    }

    private relationshipInsightForCurrentData() {
        let blockTest = this.datasetBlocks[this.selectedDatasetIndex]?.selectedTest;
        if (blockTest) {
            let detail = this.testDetailInsight(blockTest);
            return `AI가 먼저 볼 핵심 관계는 ${blockTest.label}입니다. ${blockTest.pLabel}로 ${blockTest.decision}이며, ${detail}`;
        }
        return this.strongestCorrelationInsight();
    }

    private fixedGraphSummaryForCurrentData() {
        let blockTest = this.datasetBlocks[this.selectedDatasetIndex]?.selectedTest;
        if (!blockTest) {
            if (this.numericColumns.length > 0) return '그래프 추천 영역에는 숫자형 변수 요약 그래프를 필수 예시로 먼저 고정합니다.';
            if (this.categoricalColumns.length > 0) return '그래프 추천 영역에는 범주 분포 그래프를 필수 예시로 먼저 고정합니다.';
            return '';
        }
        return `그래프 추천 영역에는 ${this.requiredGraphLabelForTest(blockTest)}를 필수 그래프로 먼저 고정합니다.`;
    }

    private testDetailInsight(test: ParallelTestResult) {
        let analysisId = this.analysisIdForParallelModel(test.model);
        if (analysisId === 'correlation' && test.predictor && test.outcome) {
            return this.correlationInsight(test.predictor, test.outcome) || test.rationale;
        }
        if ((analysisId === 'ttest' || analysisId === 'anova') && test.outcome && test.group) {
            return this.groupDifferenceInsight(test.outcome, test.group) || test.rationale;
        }
        if (analysisId === 'chisquare' && test.outcome && test.predictor) {
            return this.categoryAssociationInsight(test.outcome, test.predictor) || test.rationale;
        }
        if (analysisId === 'logistic' && test.outcome && test.predictor) {
            return this.logisticTrendInsight(test.outcome, test.predictor) || test.rationale;
        }
        return test.rationale;
    }

    private requiredGraphLabelForTest(test: ParallelTestResult) {
        let analysisId = this.analysisIdForParallelModel(test.model);
        if (analysisId === 'ttest') return `${test.group}별 ${test.outcome} 평균 비교 그래프`;
        if (analysisId === 'anova') return `${test.group}별 ${test.outcome} 집단 평균 그래프`;
        if (analysisId === 'correlation') return `${test.predictor}와 ${test.outcome} 상관 산점도`;
        if (analysisId === 'chisquare') return `${test.outcome} × ${test.predictor} 교차 막대그래프`;
        if (analysisId === 'logistic') return `${test.predictor}에 따른 ${test.outcome} 예측확률 그래프`;
        return '변수 요약 그래프';
    }

    private strongestCorrelationInsight() {
        let best: any = null;
        for (let i = 0; i < this.numericColumns.length; i++) {
            for (let j = i + 1; j < this.numericColumns.length; j++) {
                let x = this.numericColumns[i].name;
                let y = this.numericColumns[j].name;
                let pairs = this.numericPairs(x, y);
                if (pairs.length < 3) continue;
                let r = this.correlation(pairs.map((pair: any) => pair.x), pairs.map((pair: any) => pair.y));
                if (!best || Math.abs(r) > Math.abs(best.r)) best = { x, y, r, n: pairs.length };
            }
        }
        if (!best) return '';
        let direction = best.r >= 0 ? '함께 증가하는 양의 관계' : '한쪽이 증가할수록 다른 쪽이 감소하는 음의 관계';
        return `가장 강한 숫자형 관계는 ${best.x}와 ${best.y}이며, r=${this.formatNumber(best.r)}로 ${direction}입니다.`;
    }

    private correlationInsight(xName: string, yName: string) {
        let pairs = this.numericPairs(xName, yName);
        if (pairs.length < 3) return '';
        let r = this.correlation(pairs.map((pair: any) => pair.x), pairs.map((pair: any) => pair.y));
        let direction = r >= 0 ? '양의 방향' : '음의 방향';
        return `${xName}와 ${yName}는 r=${this.formatNumber(r)}로 ${direction} 관계를 보입니다.`;
    }

    private groupDifferenceInsight(outcome: string, group: string) {
        let groups = this.groupNumericValues(outcome, group).filter((item: any) => item.values.length > 0);
        if (groups.length < 2) return '';
        let summaries = groups.map((item: any) => ({ level: item.level, mean: this.mean(item.values), n: item.values.length }));
        summaries = summaries.sort((a: any, b: any) => b.mean - a.mean);
        let high = summaries[0];
        let low = summaries[summaries.length - 1];
        return `${high.level} 평균이 ${this.formatNumber(high.mean)}로 가장 높고, ${low.level} 평균은 ${this.formatNumber(low.mean)}입니다.`;
    }

    private experimentalDirectionConclusion(test: any) {
        if (!this.isExperimentalComparisonContext() || !test || !test.levels || test.levels.length < 2) return '';
        let after = test.levels.find((item: any) => /후|after|개선|이후|new|post/i.test(item.label));
        let before = test.levels.find((item: any) => /전|before|이전|old|pre/i.test(item.label));
        if (!after || !before) return '';
        let lowerIsBetter = /낮아|낮게|감소|줄|decrease|lower/.test(this.conclusionContextText().toLowerCase());
        let delta = after.mean - before.mean;
        let expected = lowerIsBetter ? delta < 0 : delta > 0;
        let direction = lowerIsBetter ? '낮아져야 하는' : '높아져야 하는';
        let observed = delta >= 0 ? '증가' : '감소';
        let significance = test.p < this.modelOptions.alpha ? '통계적으로도 유의합니다' : '통계적으로 유의하다고 보기는 어렵습니다';
        return `${test.outcome}는 개선 후 평균이 개선 전보다 ${this.formatNumber(Math.abs(delta))} ${observed}했으며, "${direction}" 기대 방향과 ${expected ? '일치합니다' : '일치하지 않습니다'}; 이 차이는 ${significance}.`;
    }

    private categoryAssociationInsight(rowName: string, columnName: string) {
        let pairs = this.categoryPairs(rowName, columnName);
        if (pairs.length === 0) return '';
        let counts: any = {};
        pairs.forEach((pair: any) => {
            let key = `${pair.row} / ${pair.column}`;
            counts[key] = (counts[key] || 0) + 1;
        });
        let top = Object.keys(counts).sort((a: string, b: string) => counts[b] - counts[a])[0];
        if (!top) return '';
        return `${top} 조합이 ${this.formatNumber(counts[top], 0)}건으로 가장 자주 나타납니다.`;
    }

    private logisticTrendInsight(outcome: string, predictor: string) {
        let levels = this.levelsFor(outcome);
        if (levels.length !== 2) return '';
        let positive = levels.indexOf(this.positiveLevel) >= 0 ? this.positiveLevel : levels[1];
        let pairs = this.rows.map((row: any) => {
            let x = this.numericValue(row, predictor);
            let yRaw = this.categoryValue(row, outcome);
            if (x === null || !yRaw) return null;
            return { x, y: yRaw === positive ? 1 : 0 };
        }).filter((item: any) => item !== null).sort((a: any, b: any) => a.x - b.x);
        if (pairs.length < 6) return '';
        let half = Math.floor(pairs.length / 2);
        let low = pairs.slice(0, half);
        let high = pairs.slice(pairs.length - half);
        let lowRate = low.reduce((sum: number, item: any) => sum + item.y, 0) / low.length;
        let highRate = high.reduce((sum: number, item: any) => sum + item.y, 0) / high.length;
        return `${predictor} 하위 구간의 ${positive} 비율은 ${this.formatNumber(lowRate * 100, 1)}%, 상위 구간은 ${this.formatNumber(highRate * 100, 1)}%입니다.`;
    }

    private conclusionPlanRecommendation(): Recommendation | null {
        let plan = this.conclusionBlueprint;
        if (!plan || !plan.recommendedAnalysis || plan.recommendedAnalysis === 'auto') return null;
        return {
            id: plan.recommendedAnalysis,
            label: this.analysisLabel(plan.recommendedAnalysis),
            reason: `AI가 배경설명과 대화로 먼저 잡은 결론 가닥입니다. ${plan.rationale}`,
            score: 99,
            outcome: plan.outcomeCandidates[0],
            predictor: ['ttest', 'anova'].indexOf(plan.recommendedAnalysis) >= 0 ? plan.groupCandidates[0] : plan.predictorCandidates[0],
            group: plan.groupCandidates[0]
        };
    }

    private mergeRecommendations(items: Recommendation[]) {
        let byId: any = {};
        items.forEach((item: Recommendation) => {
            if (!byId[item.id] || item.score > byId[item.id].score) byId[item.id] = item;
        });
        return Object.keys(byId)
            .map((id: string) => byId[id])
            .sort((a: Recommendation, b: Recommendation) => b.score - a.score)
            .slice(0, 6);
    }

    private buildRecommendations() {
        this.recommendations = [];
        if (this.columns.length === 0 || this.rows.length === 0) return;

        let numeric = this.numericColumns;
        let categorical = this.categoricalColumns;
        let binaryCategorical = categorical.filter((column: ColumnProfile) => column.distinct === 2);
        let multiCategorical = categorical.filter((column: ColumnProfile) => column.distinct > 2);
        let context = this.conclusionContextText().toLowerCase();
        let wantsDifference = /차이|비교|집단|그룹|효과|평균|전후|전 후|개선|향상|purity|recovery|수율|순도|회수율/.test(context);
        let wantsRelation = /관계|상관|연관|관련/.test(context);
        let wantsPrediction = /예측|영향|회귀|모델|요인|설명/.test(context);
        let wantsFrequency = /빈도|비율|분포|교차|독립성|카이/.test(context);
        let planRecommendation = this.conclusionPlanRecommendation();

        this.recommendations.push({
            id: 'descriptive',
            label: this.analysisLabel('descriptive'),
            reason: this.analysisNeed.needed ? '전체 변수 요약과 결측, 중심경향을 먼저 확인합니다.' : this.analysisNeed.recommendation,
            score: this.analysisNeed.needed ? (wantsFrequency ? 78 : 64) : 98
        });
        if (planRecommendation) this.recommendations.push(planRecommendation);

        if (!this.analysisNeed.needed) {
            this.recommendations = this.mergeRecommendations(this.recommendations);
            return;
        }

        if (numeric.length >= 2) {
            this.recommendations.push({
                id: 'correlation',
                label: this.analysisLabel('correlation'),
                reason: '숫자형 변수 2개 이상의 선형 관계를 확인할 수 있습니다.',
                score: wantsRelation ? 92 : 76,
                outcome: numeric[0].name,
                predictor: numeric[1].name
            });
            this.recommendations.push({
                id: 'linear',
                label: this.analysisLabel('linear'),
                reason: '숫자형 결과 변수를 숫자형 예측 변수로 설명합니다.',
                score: wantsPrediction ? 91 : 72,
                outcome: numeric[0].name,
                predictor: numeric[1].name
            });
        }

        if (numeric.length >= 1 && binaryCategorical.length >= 1) {
            this.recommendations.push({
                id: 'ttest',
                label: this.analysisLabel('ttest'),
                reason: '두 집단의 숫자형 평균 차이를 검정할 수 있습니다.',
                score: wantsDifference ? 94 : 80,
                outcome: numeric[0].name,
                group: binaryCategorical[0].name
            });
        }

        if (numeric.length >= 1 && multiCategorical.length >= 1) {
            this.recommendations.push({
                id: 'anova',
                label: this.analysisLabel('anova'),
                reason: '세 집단 이상에서 숫자형 평균 차이를 비교합니다.',
                score: wantsDifference ? 93 : 79,
                outcome: numeric[0].name,
                group: multiCategorical[0].name
            });
        }

        if (categorical.length >= 2) {
            this.recommendations.push({
                id: 'chisquare',
                label: this.analysisLabel('chisquare'),
                reason: '범주형 변수끼리 독립적인지 교차표로 검정합니다.',
                score: wantsFrequency || wantsRelation ? 89 : 73,
                outcome: categorical[0].name,
                predictor: categorical[1].name
            });
        }

        if (binaryCategorical.length >= 1 && numeric.length >= 1) {
            this.recommendations.push({
                id: 'logistic',
                label: this.analysisLabel('logistic'),
                reason: '두 범주 결과를 숫자형 예측 변수로 분류·예측합니다.',
                score: wantsPrediction ? 90 : 71,
                outcome: binaryCategorical[0].name,
                predictor: numeric[0].name
            });
        }

        this.recommendations = this.mergeRecommendations(this.recommendations);
    }

    private autoApplyRecommendation() {
        if (this.evidenceMode === 'guided') {
            this.applyConclusionBlueprint();
            if (this.selectedAnalysis !== 'auto') return;
        }
        if (this.selectedAnalysis !== 'auto') return;
        if (!this.analysisNeed.needed) {
            this.selectedAnalysis = 'descriptive';
            return;
        }

        let blockTest = this.datasetBlocks[this.selectedDatasetIndex]?.selectedTest;
        let blockAnalysis = blockTest ? this.analysisIdForParallelModel(blockTest.model) : '';
        if (blockTest && blockAnalysis) {
            this.selectedAnalysis = blockAnalysis;
            this.selectedOutcomes = blockTest.outcome ? [blockTest.outcome] : this.selectedOutcomes;
            if (['ttest', 'anova'].indexOf(blockAnalysis) >= 0) {
                let targets = this.inferNumericTargetsFromContext();
                if (targets.length > 0) this.selectedOutcomes = targets;
            }
            this.selectedPredictors = blockTest.predictor ? [blockTest.predictor] : this.selectedPredictors;
            this.selectedGroups = blockTest.group ? [blockTest.group] : this.selectedGroups;
            this.syncSinglesFromMultiSelections();
            this.syncPositiveLevel();
            return;
        }

        let item = this.recommendations[0];
        if (!item) return;
        this.applyRecommendationSelection(item);
    }

    private applyRecommendationSelection(item: Recommendation) {
        this.selectedAnalysis = item.id;
        if (['ttest', 'anova'].indexOf(item.id) >= 0) {
            let outcomes = this.inferNumericTargetsFromContext();
            this.selectedOutcomes = outcomes.length > 0 ? outcomes : (item.outcome ? [item.outcome] : this.selectedOutcomes);
            let groups = item.group ? [item.group] : this.inferGroupTargetsFromContext();
            this.selectedGroups = groups.length > 0 ? groups : this.selectedGroups;
            this.selectedPredictors = this.selectedGroups.slice();
        } else if (item.id === 'correlation') {
            let outcomes = this.inferNumericTargetsFromContext();
            let predictors = this.inferPredictorTargetsFromContext(outcomes);
            this.selectedOutcomes = outcomes.length > 0 ? outcomes : (item.outcome ? [item.outcome] : this.selectedOutcomes);
            this.selectedPredictors = predictors.length > 0 ? predictors : (item.predictor ? [item.predictor] : this.selectedPredictors);
        } else if (item.id === 'linear') {
            let outcomes = item.outcome ? [item.outcome] : this.inferNumericTargetsFromContext().slice(0, 1);
            this.selectedOutcomes = outcomes.length > 0 ? outcomes : this.selectedOutcomes;
            let predictors = this.inferPredictorTargetsFromContext(this.selectedOutcomes);
            this.selectedPredictors = predictors.length > 0 ? predictors : (item.predictor ? [item.predictor] : this.selectedPredictors);
        } else if (item.id === 'chisquare') {
            this.selectedOutcomes = item.outcome ? [item.outcome] : this.selectedOutcomes;
            this.selectedPredictors = item.predictor ? [item.predictor] : this.selectedPredictors;
        } else if (item.id === 'logistic') {
            this.selectedOutcomes = item.outcome ? [item.outcome] : this.selectedOutcomes;
            this.selectedPredictors = item.predictor ? [item.predictor] : this.selectedPredictors;
        } else {
            this.selectedOutcomes = this.inferNumericTargetsFromContext();
            this.selectedPredictors = this.inferPredictorTargetsFromContext(this.selectedOutcomes);
        }
        this.syncSinglesFromMultiSelections();
        this.syncPositiveLevel();
    }

    private analysisIdForParallelModel(model: string) {
        if (/t-test/.test(model)) return 'ttest';
        if (/분산/.test(model)) return 'anova';
        if (/상관/.test(model)) return 'correlation';
        if (/카이/.test(model)) return 'chisquare';
        if (/로지스틱/.test(model)) return 'logistic';
        return '';
    }

    private runSelected() {
        if (this.rows.length === 0 || this.columns.length === 0) {
            this.activeAnalysis = '';
            this.result = null;
            return;
        }

        let target = this.selectedAnalysis === 'auto' ? (this.recommendations[0]?.id || 'descriptive') : this.selectedAnalysis;
        this.activeAnalysis = target;

        if (target === 'correlation') this.result = this.runCorrelation();
        else if (target === 'ttest') this.result = this.runTTest();
        else if (target === 'anova') this.result = this.runAnova();
        else if (target === 'chisquare') this.result = this.runChiSquare();
        else if (target === 'linear') this.result = this.runLinearRegression();
        else if (target === 'logistic') this.result = this.runLogisticRegression();
        else this.result = this.runDescriptive();
        this.buildNormalityResults();
        this.buildModelRationale();
        this.buildManuscriptSentence();
        this.buildGraphRecommendations();
    }

    private runDescriptive(): AnalysisResult {
        let numericRows = this.numericColumns.map((column: ColumnProfile) => [
            column.name,
            this.formatNumber(column.count, 0),
            this.formatNumber(column.mean),
            this.formatNumber(column.sd),
            this.formatNumber(column.min),
            this.formatNumber(column.median),
            this.formatNumber(column.max),
            this.formatNumber(column.missing, 0)
        ]);

        let categoricalRows = this.categoricalColumns.map((column: ColumnProfile) => [
            column.name,
            this.formatNumber(column.count, 0),
            this.formatNumber(column.distinct, 0),
            column.levels.slice(0, 5).join(', ') || '-',
            this.formatNumber(column.missing, 0)
        ]);
        let totalMissing = this.columns.reduce((sum: number, column: ColumnProfile) => sum + column.missing, 0);
        let mostMissing = this.columns.slice().sort((a: ColumnProfile, b: ColumnProfile) => b.missing - a.missing)[0];
        let beginnerRows = [
            ['숫자형 변수', '평균, 표준편차, 최소/최대값을 봅니다. 표준편차가 크면 값이 넓게 퍼져 있다는 뜻입니다.'],
            ['범주형 변수', '각 범주가 몇 번 나오는지 봅니다. 집단 비교나 비율 비교의 후보가 됩니다.'],
            ['결측', '비어 있는 값입니다. 결측이 많으면 분석 결과가 흔들릴 수 있습니다.'],
            ['다음 단계', '비교하고 싶은 집단이 있으면 t-test 또는 ANOVA, 함께 움직이는 숫자형 변수가 궁금하면 상관분석을 봅니다.']
        ];

        return {
            title: this.analysisLabel('descriptive'),
            subtitle: `${this.rows.length}행, ${this.columns.length}개 변수`,
            metrics: [
                { label: '행 수', value: this.formatNumber(this.rows.length, 0), accent: 'text-gray-900' },
                { label: '숫자형', value: this.formatNumber(this.numericColumns.length, 0), accent: 'text-sky-700' },
                { label: '범주형', value: this.formatNumber(this.categoricalColumns.length, 0), accent: 'text-emerald-700' },
                { label: '총 결측', value: this.formatNumber(totalMissing, 0), accent: totalMissing > 0 ? 'text-amber-700' : 'text-gray-900' }
            ],
            tables: [
                { title: '처음 보는 사람을 위한 읽는 법', headers: ['항목', '의미'], rows: beginnerRows },
                { title: '숫자형 변수', headers: ['변수', 'N', '평균', '표준편차', '최소', '중앙값', '최대', '결측'], rows: numericRows },
                { title: '범주형 변수', headers: ['변수', 'N', '범주 수', '주요 범주', '결측'], rows: categoricalRows }
            ].filter((table: any) => table.rows.length > 0),
            notes: [
                '기술통계/빈도분석은 데이터를 처음 열었을 때 반드시 보는 안전 점검 단계입니다.',
                mostMissing && mostMissing.missing > 0 ? `${mostMissing.name} 변수의 결측이 ${mostMissing.missing}개로 가장 많습니다.` : '현재 표에서 눈에 띄는 결측은 없습니다.'
            ],
            conclusion: `숫자형 ${this.numericColumns.length}개, 범주형 ${this.categoricalColumns.length}개로 인식했으며, 총 결측은 ${this.formatNumber(totalMissing, 0)}개입니다.`,
            status: 'ready',
            paper: {
                type: 'descriptive',
                n: this.rows.length,
                variableCount: this.columns.length,
                numericCount: this.numericColumns.length,
                categoricalCount: this.categoricalColumns.length
            }
        };
    }

    private runCorrelation(): AnalysisResult {
        let selected = this.cleanSelection(this.selectedOutcomes.concat(this.selectedPredictors), this.numericColumns.map((column: ColumnProfile) => column.name), []);
        if (selected.length < 2) selected = this.numericColumns.map((column: ColumnProfile) => column.name);
        if (selected.length < 2) return this.requirement('상관분석', '상관분석에는 숫자형 변수가 최소 2개 필요합니다.');

        let analyses: any[] = [];
        for (let i = 0; i < selected.length; i++) {
            for (let j = i + 1; j < selected.length; j++) {
                let x = selected[i];
                let y = selected[j];
                let pairs = this.numericPairs(x, y);
                if (pairs.length < 3) continue;
                let xs = pairs.map((pair: any) => pair.x);
                let ys = pairs.map((pair: any) => pair.y);
                let r = this.correlation(xs, ys);
                let df = pairs.length - 2;
                let t = r * Math.sqrt(df / Math.max(1e-12, 1 - r * r));
                let p = this.tTwoTailP(Math.abs(t), df);
                analyses.push({ x, y, r, t, df, p, n: pairs.length });
            }
        }
        if (analyses.length === 0) return this.requirement('상관분석', '결측을 제외한 유효한 숫자형 변수 쌍이 부족합니다.');

        analyses = analyses.sort((a: any, b: any) => Math.abs(b.r) - Math.abs(a.r));
        let top = analyses[0];
        let significantCount = analyses.filter((item: any) => item.p < this.modelOptions.alpha).length;
        let p = top.p;

        return {
            title: this.analysisLabel('correlation'),
            subtitle: `${analyses.length}개 숫자형 변수쌍 상관분석`,
            metrics: [
                { label: '가장 강한 r', value: this.formatNumber(top.r), accent: 'text-indigo-700' },
                { label: '해당 p', value: this.formatPWithStars(top.p), accent: this.pAccent(top.p) },
                { label: '분석 쌍', value: this.formatNumber(analyses.length, 0), accent: 'text-gray-900' },
                { label: '유의 쌍', value: this.formatNumber(significantCount, 0), accent: significantCount > 0 ? 'text-emerald-700' : 'text-gray-700' }
            ],
            tables: [
                { title: '처음 보는 사람을 위한 읽는 법', headers: ['항목', '의미'], rows: [
                    ['r > 0', '한 변수가 커질수록 다른 변수도 커지는 경향입니다.'],
                    ['r < 0', '한 변수가 커질수록 다른 변수는 작아지는 경향입니다.'],
                    ['p', '관계가 우연으로 보기 어려운지 판단하는 값입니다. 보통 p < .05를 유의하다고 봅니다.']
                ] },
                { title: '상관 결과', headers: ['변수 X', '변수 Y', 'r', '방향', 't', 'df', 'p', 'N'], rows: analyses.map((item: any) => [
                    item.x,
                    item.y,
                    this.formatNumber(item.r),
                    item.r >= 0 ? '양의 관계' : '음의 관계',
                    this.formatNumber(item.t),
                    this.formatNumber(item.df, 0),
                    this.formatPWithStars(item.p),
                    this.formatNumber(item.n, 0)
                ]) }
            ],
            notes: ['Pearson 상관은 두 숫자형 변수가 직선 형태로 함께 움직이는지를 봅니다.', '상관은 원인-결과를 증명하지 않으므로, 원인 해석은 실험 설계나 회귀분석과 함께 확인하는 것이 좋습니다.'],
            conclusion: this.significanceConclusion(p, `${top.x}와 ${top.y}의 상관계수는 ${this.formatNumber(top.r)}입니다.`),
            pValue: p,
            status: 'ready',
            paper: analyses.length === 1
                ? { type: 'correlation', ...top }
                : { type: 'multi_correlation', tests: analyses, top, p }
        };
    }

    private runTTest(): AnalysisResult {
        let outcomes = this.cleanSelection(this.selectedOutcomes, this.numericColumns.map((column: ColumnProfile) => column.name), [this.requireNumeric(this.outcome)]);
        let groupNames = this.cleanSelection(this.selectedGroups, this.categoricalColumns.map((column: ColumnProfile) => column.name), [this.requireCategorical(this.group || this.predictor)]);
        if (outcomes.length === 0 || groupNames.length === 0) return this.requirement('독립표본 t-test', '숫자형 결과 변수와 두 범주의 집단 변수를 하나 이상 선택하세요.');

        let analyses: any[] = [];
        outcomes.forEach((outcome: string) => {
            groupNames.forEach((group: string) => {
                let groups = this.groupNumericValues(outcome, group).filter((item: any) => item.values.length > 0);
                if (groups.length !== 2 || groups[0].values.length < 2 || groups[1].values.length < 2) return;
                let a = groups[0];
                let b = groups[1];
                let meanA = this.mean(a.values);
                let meanB = this.mean(b.values);
                let varA = this.variance(a.values);
                let varB = this.variance(b.values);
                let se = Math.sqrt(varA / a.values.length + varB / b.values.length);
                if (se === 0) return;
                let t = (meanA - meanB) / se;
                let numerator = Math.pow(varA / a.values.length + varB / b.values.length, 2);
                let denominator = (Math.pow(varA / a.values.length, 2) / (a.values.length - 1)) + (Math.pow(varB / b.values.length, 2) / (b.values.length - 1));
                let df = numerator / denominator;
                let p = this.tTwoTailP(Math.abs(t), df);
                let pooled = Math.sqrt(((a.values.length - 1) * varA + (b.values.length - 1) * varB) / (a.values.length + b.values.length - 2));
                let d = pooled === 0 ? 0 : (meanA - meanB) / pooled;
                analyses.push({
                    outcome,
                    group,
                    levels: [
                        { label: a.level, n: a.values.length, mean: meanA, sd: this.standardDeviation(a.values) },
                        { label: b.level, n: b.values.length, mean: meanB, sd: this.standardDeviation(b.values) }
                    ],
                    meanDiff: meanA - meanB,
                    se,
                    t,
                    df,
                    p,
                    d
                });
            });
        });
        if (analyses.length === 0) return this.requirement('독립표본 t-test', '선택한 집단 변수는 결측 제외 후 정확히 2개 범주여야 하며, 각 집단에 관측치가 최소 2개씩 필요합니다.');

        analyses = analyses.sort((a: any, b: any) => a.p - b.p);
        let top = analyses[0];
        let significantCount = analyses.filter((item: any) => item.p < this.modelOptions.alpha).length;
        let p = top.p;

        return {
            title: this.analysisLabel('ttest'),
            subtitle: `${outcomes.length}개 결과 변수 × ${groupNames.length}개 집단 변수 평균 비교`,
            metrics: [
                { label: '검정 수', value: this.formatNumber(analyses.length, 0), accent: 'text-gray-900' },
                { label: '가장 작은 p', value: this.formatPWithStars(top.p), accent: this.pAccent(top.p) },
                { label: '유의 결과', value: this.formatNumber(significantCount, 0), accent: significantCount > 0 ? 'text-emerald-700' : 'text-gray-700' },
                { label: '최대 |d|', value: this.modelOptions.effectSize ? this.formatNumber(Math.max(...analyses.map((item: any) => Math.abs(item.d)))) : '-', accent: 'text-sky-700' }
            ],
            tables: [
                { title: '처음 보는 사람을 위한 읽는 법', headers: ['항목', '의미'], rows: [
                    ['평균 차이', '첫 번째 집단 평균에서 두 번째 집단 평균을 뺀 값입니다.'],
                    ['p', '두 집단 평균 차이가 우연으로 보기 어려운지 판단합니다.'],
                    ['Cohen d', '차이의 크기입니다. 절댓값이 클수록 차이가 큽니다.']
                ] },
                { title: '결과 변수별 Welch t-test', headers: ['결과 변수', '집단 변수', '집단 1', '집단 2', '평균 차이', 't', 'df', 'p', 'Cohen d'], rows: analyses.map((item: any) => [
                    item.outcome,
                    item.group,
                    `${item.levels[0].label} M=${this.formatNumber(item.levels[0].mean)}, SD=${this.formatNumber(item.levels[0].sd)}, n=${this.formatNumber(item.levels[0].n, 0)}`,
                    `${item.levels[1].label} M=${this.formatNumber(item.levels[1].mean)}, SD=${this.formatNumber(item.levels[1].sd)}, n=${this.formatNumber(item.levels[1].n, 0)}`,
                    this.formatNumber(item.meanDiff),
                    this.formatNumber(item.t),
                    this.formatNumber(item.df),
                    this.formatPWithStars(item.p),
                    this.modelOptions.effectSize ? this.formatNumber(item.d) : '-'
                ]) }
            ],
            notes: ['등분산을 가정하지 않는 Welch t-test로 계산했습니다.', this.experimentalDirectionConclusion(top) || (this.isExperimentalComparisonContext() ? this.desiredDirectionText() : '두 집단 중 어느 쪽이 더 큰지는 집단 평균을 함께 확인하세요.')],
            conclusion: this.experimentalDirectionConclusion(top) || this.significanceConclusion(p, `${top.group}에 따른 ${top.outcome} 평균 차이의 t 값은 ${this.formatNumber(top.t)}입니다.`),
            pValue: p,
            status: 'ready',
            paper: analyses.length === 1 ? { type: 'ttest', ...top } : { type: 'multi_ttest', tests: analyses, top, p }
        };
    }

    private runAnova(): AnalysisResult {
        let outcomes = this.cleanSelection(this.selectedOutcomes, this.numericColumns.map((column: ColumnProfile) => column.name), [this.requireNumeric(this.outcome)]);
        let groupNames = this.cleanSelection(this.selectedGroups, this.categoricalColumns.map((column: ColumnProfile) => column.name), [this.requireCategorical(this.group || this.predictor)]);
        if (outcomes.length === 0 || groupNames.length === 0) return this.requirement('일원분산분석', '숫자형 결과 변수와 세 범주 이상의 집단 변수를 하나 이상 선택하세요.');

        let analyses: any[] = [];
        outcomes.forEach((outcome: string) => {
            groupNames.forEach((group: string) => {
                let groups = this.groupNumericValues(outcome, group).filter((item: any) => item.values.length > 0);
                if (groups.length < 3) return;
                let allValues: number[] = [];
                groups.forEach((item: any) => allValues = allValues.concat(item.values));
                if (allValues.length <= groups.length) return;
                let grandMean = this.mean(allValues);
                let ssBetween = groups.reduce((sum: number, item: any) => sum + item.values.length * Math.pow(this.mean(item.values) - grandMean, 2), 0);
                let ssWithin = groups.reduce((sum: number, item: any) => sum + item.values.reduce((inner: number, value: number) => inner + Math.pow(value - this.mean(item.values), 2), 0), 0);
                let dfBetween = groups.length - 1;
                let dfWithin = allValues.length - groups.length;
                let msBetween = ssBetween / dfBetween;
                let msWithin = ssWithin / dfWithin;
                let f = msWithin === 0 ? 0 : msBetween / msWithin;
                let p = this.fPValue(f, dfBetween, dfWithin);
                let etaSquared = (ssBetween + ssWithin) === 0 ? 0 : ssBetween / (ssBetween + ssWithin);
                analyses.push({
                    outcome,
                    group,
                    groups: groups.map((item: any) => ({ label: item.level, n: item.values.length, mean: this.mean(item.values), sd: this.standardDeviation(item.values) })),
                    f,
                    dfBetween,
                    dfWithin,
                    p,
                    etaSquared
                });
            });
        });
        if (analyses.length === 0) return this.requirement('일원분산분석', '선택한 집단 변수는 결측 제외 후 3개 이상 범주여야 하며, 집단 내 자유도가 충분해야 합니다.');

        analyses = analyses.sort((a: any, b: any) => a.p - b.p);
        let top = analyses[0];
        let significantCount = analyses.filter((item: any) => item.p < this.modelOptions.alpha).length;
        let p = top.p;

        return {
            title: this.analysisLabel('anova'),
            subtitle: `${outcomes.length}개 결과 변수 × ${groupNames.length}개 집단 변수 평균 비교`,
            metrics: [
                { label: '검정 수', value: this.formatNumber(analyses.length, 0), accent: 'text-gray-900' },
                { label: '가장 작은 p', value: this.formatPWithStars(top.p), accent: this.pAccent(top.p) },
                { label: '유의 결과', value: this.formatNumber(significantCount, 0), accent: significantCount > 0 ? 'text-emerald-700' : 'text-gray-700' },
                { label: '최대 η²', value: this.modelOptions.effectSize ? this.formatNumber(Math.max(...analyses.map((item: any) => item.etaSquared))) : '-', accent: 'text-sky-700' }
            ],
            tables: [
                { title: '처음 보는 사람을 위한 읽는 법', headers: ['항목', '의미'], rows: [
                    ['F', '집단 평균 차이가 집단 내 변동에 비해 얼마나 큰지 보는 값입니다.'],
                    ['p', '세 집단 이상 평균 차이가 우연으로 보기 어려운지 판단합니다.'],
                    ['η²', '집단 차이가 전체 변동에서 차지하는 비율입니다.']
                ] },
                { title: '결과 변수별 ANOVA', headers: ['결과 변수', '집단 변수', '집단 요약', 'F', 'df', 'p', 'η²'], rows: analyses.map((item: any) => [
                    item.outcome,
                    item.group,
                    item.groups.map((group: any) => `${group.label} M=${this.formatNumber(group.mean)}, SD=${this.formatNumber(group.sd)}, n=${this.formatNumber(group.n, 0)}`).join(' / '),
                    this.formatNumber(item.f),
                    `${this.formatNumber(item.dfBetween, 0)}, ${this.formatNumber(item.dfWithin, 0)}`,
                    this.formatPWithStars(item.p),
                    this.modelOptions.effectSize ? this.formatNumber(item.etaSquared) : '-'
                ]) }
            ],
            notes: ['사후검정은 포함하지 않았습니다. 유의하면 집단별 평균표를 기준으로 후속 비교가 필요합니다.'],
            conclusion: this.significanceConclusion(p, `${top.group}에 따른 ${top.outcome} 평균 차이 검정의 F 값은 ${this.formatNumber(top.f)}입니다.`),
            pValue: p,
            status: 'ready',
            paper: analyses.length === 1 ? { type: 'anova', ...top } : { type: 'multi_anova', tests: analyses, top, p }
        };
    }

    private runChiSquare(): AnalysisResult {
        let rowName = this.requireCategorical(this.outcome);
        let columnName = this.requireCategorical(this.predictor);
        if (!rowName || !columnName || rowName === columnName) return this.requirement('카이제곱 검정', '서로 다른 범주형 변수 2개를 선택하세요.');

        let pairs = this.categoryPairs(rowName, columnName);
        if (pairs.length === 0) return this.requirement('카이제곱 검정', '두 변수의 유효한 관측치가 없습니다.');

        let rowLevels = Array.from(new Set(pairs.map((pair: any) => pair.row)));
        let columnLevels = Array.from(new Set(pairs.map((pair: any) => pair.column)));
        if (rowLevels.length < 2 || columnLevels.length < 2) return this.requirement('카이제곱 검정', '각 변수에 최소 2개 범주가 필요합니다.');

        let matrix = rowLevels.map((row: any) => columnLevels.map((column: any) => pairs.filter((pair: any) => pair.row === row && pair.column === column).length));
        let rowTotals = matrix.map((row: number[]) => row.reduce((sum: number, value: number) => sum + value, 0));
        let columnTotals = columnLevels.map((_: any, index: number) => matrix.reduce((sum: number, row: number[]) => sum + row[index], 0));
        let total = pairs.length;
        let chi = 0;
        matrix.forEach((row: number[], rowIndex: number) => {
            row.forEach((observed: number, columnIndex: number) => {
                let expected = rowTotals[rowIndex] * columnTotals[columnIndex] / total;
                if (expected > 0) chi += Math.pow(observed - expected, 2) / expected;
            });
        });
        let df = (rowLevels.length - 1) * (columnLevels.length - 1);
        let p = this.chiSquarePValue(chi, df);
        let cramer = Math.sqrt(chi / (total * Math.max(1, Math.min(rowLevels.length - 1, columnLevels.length - 1))));
        let tableRows = rowLevels.map((row: any, index: number) => [row].concat(matrix[index].map((value: number) => this.formatNumber(value, 0))).concat([this.formatNumber(rowTotals[index], 0)]));

        return {
            title: this.analysisLabel('chisquare'),
            subtitle: `${rowName} × ${columnName}`,
            metrics: [
                { label: 'χ²', value: this.formatNumber(chi), accent: 'text-indigo-700' },
                { label: 'p', value: this.formatPWithStars(p), accent: this.pAccent(p) },
                { label: 'df', value: this.formatNumber(df, 0), accent: 'text-gray-900' },
                { label: 'Cramer V', value: this.modelOptions.effectSize ? this.formatNumber(cramer) : '-', accent: 'text-sky-700' }
            ],
            tables: [
                { title: '교차표', headers: [rowName].concat(columnLevels).concat(['합계']), rows: tableRows },
                { title: '검정', headers: ['χ²', 'df', 'p', 'N'], rows: [[this.formatNumber(chi), this.formatNumber(df, 0), this.formatPWithStars(p), this.formatNumber(total, 0)]] }
            ],
            notes: ['기대빈도가 작은 셀이 많으면 Fisher exact test 같은 대안 검정이 필요할 수 있습니다.'],
            conclusion: this.significanceConclusion(p, `${rowName}와 ${columnName}의 독립성 검정 결과 χ²=${this.formatNumber(chi)}입니다.`),
            pValue: p,
            status: 'ready',
            paper: { type: 'chisquare', rowName, columnName, chi, df, p, n: total, cramer }
        };
    }

    private runLinearRegression(): AnalysisResult {
        let outcomes = this.cleanSelection(this.selectedOutcomes, this.numericColumns.map((column: ColumnProfile) => column.name), [this.requireNumeric(this.outcome)]);
        let predictors = this.cleanSelection(this.selectedPredictors, this.numericColumns.map((column: ColumnProfile) => column.name), [this.requireNumeric(this.predictor, outcomes)]);
        if (outcomes.length === 0 || predictors.length === 0) return this.requirement('선형회귀', '숫자형 결과 변수와 숫자형 예측 변수를 하나 이상 선택하세요.');

        let analyses: any[] = [];
        outcomes.forEach((yName: string) => {
            predictors.forEach((xName: string) => {
                if (xName === yName) return;
                let pairs = this.numericPairs(xName, yName);
                if (pairs.length < 3) return;
                let xs = pairs.map((pair: any) => pair.x);
                let ys = pairs.map((pair: any) => pair.y);
                let xMean = this.mean(xs);
                let yMean = this.mean(ys);
                let sxx = xs.reduce((sum: number, value: number) => sum + Math.pow(value - xMean, 2), 0);
                let sxy = xs.reduce((sum: number, value: number, index: number) => sum + (value - xMean) * (ys[index] - yMean), 0);
                if (sxx === 0) return;
                let slope = sxy / sxx;
                let intercept = this.modelOptions.includeIntercept ? yMean - slope * xMean : 0;
                if (!this.modelOptions.includeIntercept) slope = xs.reduce((sum: number, value: number, index: number) => sum + value * ys[index], 0) / xs.reduce((sum: number, value: number) => sum + value * value, 0);
                let predictions = xs.map((value: number) => intercept + slope * value);
                let residuals = ys.map((value: number, index: number) => value - predictions[index]);
                let sse = residuals.reduce((sum: number, value: number) => sum + value * value, 0);
                let sst = ys.reduce((sum: number, value: number) => sum + Math.pow(value - yMean, 2), 0);
                let df = pairs.length - (this.modelOptions.includeIntercept ? 2 : 1);
                let mse = sse / Math.max(1, df);
                let seSlope = Math.sqrt(mse / sxx);
                let t = seSlope === 0 ? 0 : slope / seSlope;
                let p = this.tTwoTailP(Math.abs(t), Math.max(1, df));
                let r2 = sst === 0 ? 0 : 1 - sse / sst;
                let standardized = this.correlation(xs, ys);
                analyses.push({ xName, yName, intercept, slope, seSlope, t, df, p, r2, n: pairs.length, standardized });
            });
        });
        if (analyses.length === 0) return this.requirement('선형회귀', '선택한 변수 조합에서 회귀분석에 필요한 유효 관측치가 부족합니다.');

        analyses = analyses.sort((a: any, b: any) => b.r2 - a.r2);
        let top = analyses[0];
        let significantCount = analyses.filter((item: any) => item.p < this.modelOptions.alpha).length;
        let p = top.p;

        return {
            title: this.analysisLabel('linear'),
            subtitle: `${outcomes.length}개 결과 변수 × ${predictors.length}개 예측 변수 단순회귀`,
            metrics: [
                { label: '최대 R²', value: this.formatNumber(top.r2), accent: 'text-indigo-700' },
                { label: '해당 p', value: this.formatPWithStars(top.p), accent: this.pAccent(top.p) },
                { label: '모형 수', value: this.formatNumber(analyses.length, 0), accent: 'text-gray-900' },
                { label: '유의 모형', value: this.formatNumber(significantCount, 0), accent: significantCount > 0 ? 'text-emerald-700' : 'text-gray-700' }
            ],
            tables: [
                { title: '처음 보는 사람을 위한 읽는 법', headers: ['항목', '의미'], rows: [
                    ['B', '예측 변수가 1 증가할 때 결과 변수가 얼마나 변하는지 나타냅니다.'],
                    ['R²', '예측 변수가 결과 변동을 얼마나 설명하는지 나타냅니다.'],
                    ['p', '예측 변수의 기울기가 0이 아니라고 볼 수 있는지 판단합니다.']
                ] },
                { title: '단순 선형회귀 결과', headers: ['결과 변수', '예측 변수', 'B', 'SE', 't', 'df', 'p', 'R²', 'N'], rows: analyses.map((item: any) => [
                    item.yName,
                    item.xName,
                    this.formatNumber(item.slope),
                    this.formatNumber(item.seSlope),
                    this.formatNumber(item.t),
                    this.formatNumber(item.df, 0),
                    this.formatPWithStars(item.p),
                    this.formatNumber(item.r2),
                    this.formatNumber(item.n, 0)
                ]) }
            ],
            notes: ['각 결과 변수와 예측 변수 조합을 단순 선형회귀로 따로 계산합니다.', '여러 예측 변수를 동시에 넣는 다중회귀와는 다르며, 후보 변수를 빠르게 찾는 용도입니다.'],
            conclusion: this.significanceConclusion(p, `${top.xName}의 ${top.yName}에 대한 회귀계수는 ${this.formatNumber(top.slope)}이며 R²=${this.formatNumber(top.r2)}입니다.`),
            pValue: p,
            status: 'ready',
            paper: analyses.length === 1 ? { type: 'linear', ...top } : { type: 'multi_linear', tests: analyses, top, p }
        };
    }

    private runLogisticRegression(): AnalysisResult {
        let yName = this.requireCategorical(this.outcome);
        let xName = this.requireNumeric(this.predictor);
        if (!yName || !xName) return this.requirement('로지스틱 회귀', '두 범주 결과 변수와 숫자형 예측 변수를 선택하세요.');

        let levels = this.levelsFor(yName);
        if (levels.length !== 2) return this.requirement('로지스틱 회귀', '결과 변수는 결측 제외 후 정확히 2개 범주여야 합니다.');
        let positive = this.positiveLevel || levels[1];
        let pairs = this.rows.map((row: any) => {
            let x = this.numericValue(row, xName);
            let yRaw = this.categoryValue(row, yName);
            if (x === null || !yRaw || levels.indexOf(yRaw) < 0) return null;
            return { x, y: yRaw === positive ? 1 : 0 };
        }).filter((item: any) => item !== null);
        if (pairs.length < 6) return this.requirement('로지스틱 회귀', '로지스틱 회귀에는 결측 제외 후 최소 6개 관측치가 필요합니다.');

        let xs = pairs.map((pair: any) => pair.x);
        let ys = pairs.map((pair: any) => pair.y);
        let xMean = this.mean(xs);
        let xSd = this.standardDeviation(xs) || 1;
        let zs = xs.map((value: number) => (value - xMean) / xSd);
        let positiveRate = (ys.reduce((sum: number, value: number) => sum + value, 0) + 0.5) / (ys.length + 1);
        let b0 = Math.log(positiveRate / (1 - positiveRate));
        let b1 = 0;
        let learningRate = 0.08;

        for (let step = 0; step < 4000; step++) {
            let g0 = 0;
            let g1 = 0;
            zs.forEach((z: number, index: number) => {
                let p = this.sigmoid(b0 + b1 * z);
                g0 += ys[index] - p;
                g1 += (ys[index] - p) * z;
            });
            b0 += learningRate * g0 / zs.length;
            b1 += learningRate * g1 / zs.length;
        }

        let probs = zs.map((z: number) => this.sigmoid(b0 + b1 * z));
        let accuracy = probs.filter((p: number, index: number) => (p >= 0.5 ? 1 : 0) === ys[index]).length / probs.length;
        let logLikelihood = probs.reduce((sum: number, p: number, index: number) => {
            let clipped = Math.min(1 - 1e-9, Math.max(1e-9, p));
            return sum + ys[index] * Math.log(clipped) + (1 - ys[index]) * Math.log(1 - clipped);
        }, 0);

        let info00 = 0;
        let info01 = 0;
        let info11 = 0;
        probs.forEach((p: number, index: number) => {
            let w = p * (1 - p);
            info00 += w;
            info01 += w * zs[index];
            info11 += w * zs[index] * zs[index];
        });
        let det = info00 * info11 - info01 * info01;
        let seB1 = det <= 0 ? 0 : Math.sqrt(info00 / det);
        let zScore = seB1 === 0 ? 0 : b1 / seB1;
        let pValue = 2 * (1 - this.normalCdf(Math.abs(zScore)));
        let oddsRatio = Math.exp(b1);

        return {
            title: this.analysisLabel('logistic'),
            subtitle: `${positive} 확률 ~ ${xName}`,
            metrics: [
                { label: 'Odds ratio', value: this.formatNumber(oddsRatio), accent: 'text-indigo-700' },
                { label: 'p', value: this.formatPWithStars(pValue), accent: this.pAccent(pValue) },
                { label: '정확도', value: `${this.formatNumber(accuracy * 100, 1)}%`, accent: 'text-emerald-700' },
                { label: 'N', value: this.formatNumber(pairs.length, 0), accent: 'text-gray-900' }
            ],
            tables: [
                { title: '계수', headers: ['항', 'B', 'SE', 'z', 'p', 'OR'], rows: [
                    ['절편', this.formatNumber(b0), '-', '-', '-', '-'],
                    [`${xName} (1 SD)`, this.formatNumber(b1), this.formatNumber(seB1), this.formatNumber(zScore), this.formatPWithStars(pValue), this.formatNumber(oddsRatio)]
                ] },
                { title: '모형 요약', headers: ['양성 범주', '기준화 평균', '기준화 SD', 'Log likelihood'], rows: [[positive, this.formatNumber(xMean), this.formatNumber(xSd), this.formatNumber(logLikelihood)]] }
            ],
            notes: ['예측 변수는 1 표준편차 단위로 표준화해 적합했습니다.'],
            conclusion: this.significanceConclusion(pValue, `${xName}이 1 표준편차 증가할 때 ${positive}의 odds ratio는 ${this.formatNumber(oddsRatio)}입니다.`),
            pValue: pValue,
            status: 'ready',
            paper: {
                type: 'logistic',
                yName,
                xName,
                positive,
                b0,
                b1,
                seB1,
                zScore,
                p: pValue,
                oddsRatio,
                accuracy,
                n: pairs.length,
                xMean,
                xSd
            }
        };
    }

    private buildNormalityResults() {
        this.normalityResults = this.numericColumns.map((column: ColumnProfile) => {
            let values = this.rows.map((row: any) => this.parseNumber(row[column.name])).filter((value: number | null) => value !== null) as number[];
            return this.normalityTest(column.name, values);
        });

        if (this.result && this.normalityResults.length > 0) {
            this.result.tables.push({
                title: '정규성 검정',
                headers: ['변수', 'N', '통계량', 'p', '판정'],
                rows: this.normalityResults.map((item: NormalityResult) => [
                    item.variable,
                    this.formatNumber(item.n, 0),
                    this.formatNumber(item.statistic),
                    this.formatPWithStars(item.pValue),
                    item.interpretation
                ])
            });
        }
    }

    private normalityTest(variable: string, values: number[]): NormalityResult {
        let n = values.length;
        if (n < 3) {
            return {
                variable,
                n,
                statistic: 0,
                pValue: 1,
                stars: 'n.s.',
                normal: true,
                interpretation: '표본 수 부족'
            };
        }

        let avg = this.mean(values);
        let sd = this.standardDeviation(values);
        if (sd === 0) {
            return {
                variable,
                n,
                statistic: 0,
                pValue: 1,
                stars: 'n.s.',
                normal: true,
                interpretation: '분산 없음'
            };
        }

        let skew = values.reduce((sum: number, value: number) => sum + Math.pow((value - avg) / sd, 3), 0) / n;
        let kurtosis = values.reduce((sum: number, value: number) => sum + Math.pow((value - avg) / sd, 4), 0) / n - 3;
        let jb = n / 6 * (skew * skew + (kurtosis * kurtosis) / 4);
        let p = this.chiSquarePValue(jb, 2);
        let normal = p >= this.modelOptions.alpha;
        return {
            variable,
            n,
            statistic: jb,
            pValue: p,
            stars: this.pStars(p),
            normal,
            interpretation: normal ? '정규성 가정 유지' : '정규성 주의'
        };
    }

    private buildModelRationale() {
        this.modelRationale = [];
        if (!this.result) return;

        if (!this.analysisNeed.needed) {
            this.modelRationale.push(`${this.analysisNeed.label}: ${this.analysisNeed.reason}`);
            this.modelRationale.push(this.analysisNeed.recommendation);
            this.modelRationale.push('정규성 검정은 후속 분석 가능성을 확인하기 위한 참고값으로만 표시합니다.');
            return;
        }

        let outcomeNormality = this.normalityResults.find((item: NormalityResult) => item.variable === this.outcome) || this.normalityResults[0];
        let normalityText = outcomeNormality ? `${outcomeNormality.variable} 정규성 검정 p=${this.formatPWithStars(outcomeNormality.pValue)}로 ${outcomeNormality.interpretation}` : '숫자형 결과 변수가 없어 정규성 검정은 모델 선택에 직접 사용하지 않았습니다.';

        if (['ttest', 'anova'].indexOf(this.activeAnalysis) >= 0) {
            this.modelRationale.push(normalityText);
            if (outcomeNormality && outcomeNormality.normal) {
                this.modelRationale.push('정규성 가정이 유지되어 평균 차이 기반 모수 검정을 우선 적용했습니다.');
            } else {
                this.modelRationale.push('정규성 가정이 약해 비모수 대안도 함께 검토해야 합니다. 현재 결과는 모수 검정 기준 참고값입니다.');
            }
        } else if (['correlation', 'linear'].indexOf(this.activeAnalysis) >= 0) {
            this.modelRationale.push(normalityText);
            if (outcomeNormality && outcomeNormality.normal) {
                this.modelRationale.push('주요 숫자형 변수의 정규성 가정이 크게 위배되지 않아 Pearson/선형 모델을 우선 적용했습니다.');
            } else {
                this.modelRationale.push('정규성 주의가 있어 Spearman 상관 또는 강건 회귀를 후속 대안으로 검토하는 것이 좋습니다.');
            }
        } else if (this.activeAnalysis === 'chisquare') {
            this.modelRationale.push('범주형 변수 간 관계 분석이므로 정규성 검정보다는 교차표의 기대빈도 조건을 우선 확인합니다.');
        } else if (this.activeAnalysis === 'logistic') {
            this.modelRationale.push('이진 결과 변수 모델이므로 결과 변수 정규성은 요구하지 않으며, 숫자형 예측 변수의 분포는 영향점 확인에 사용합니다.');
            if (outcomeNormality) this.modelRationale.push(normalityText);
        } else {
            this.modelRationale.push('기술통계 단계에서도 숫자형 변수 정규성 검정을 함께 수행해 후속 분석 모델 선택 근거로 사용합니다.');
        }
    }

    private buildManuscriptSentence() {
        if (!this.result) {
            this.manuscriptSentence = '';
            return;
        }

        if (!this.analysisNeed.needed) {
            this.manuscriptSentence = `본 데이터는 추론통계보다 기술통계 보고가 적합하다고 판단되어, 연속형 변수는 평균과 표준편차로, 범주형 변수는 빈도와 백분율로 요약하였다.`;
            return;
        }

        let paper = this.result.paper;
        if (!paper) {
            this.manuscriptSentence = this.result.conclusion;
            return;
        }

        let assumption = ['ttest', 'anova', 'correlation', 'linear'].indexOf(paper.type) >= 0 ? '정규성 검정 결과를 확인한 후 ' : '';
        if (paper.type === 'descriptive') {
            this.manuscriptSentence = `총 ${this.formatNumber(paper.n, 0)}개의 관측치와 ${this.formatNumber(paper.variableCount, 0)}개의 변수를 분석하였으며, 연속형 변수는 평균과 표준편차로, 범주형 변수는 빈도와 백분율로 제시하였다.`;
        } else if (paper.type === 'correlation') {
            let direction = paper.r >= 0 ? '양의' : '음의';
            this.manuscriptSentence = `${assumption}Pearson 상관분석을 실시한 결과, ${paper.x}와 ${paper.y} 간 ${direction} 상관은 ${this.paperSignificancePhrase(paper.p)} (r = ${this.formatStatPaper(paper.r)}, t(${this.formatDfPaper(paper.df)}) = ${this.formatStatPaper(paper.t)}, ${this.formatPPaper(paper.p)}).`;
        } else if (paper.type === 'multi_correlation') {
            let top = paper.top;
            let significantCount = paper.tests.filter((item: any) => item.p < this.modelOptions.alpha).length;
            this.manuscriptSentence = `${assumption}총 ${this.formatNumber(paper.tests.length, 0)}개의 변수쌍에 대해 Pearson 상관분석을 실시하였다. 가장 큰 상관은 ${top.x}와 ${top.y} 간 관계에서 관찰되었으며 (r = ${this.formatStatPaper(top.r)}, t(${this.formatDfPaper(top.df)}) = ${this.formatStatPaper(top.t)}, ${this.formatPPaper(top.p)}), ${this.formatNumber(significantCount, 0)}개 변수쌍이 통계적으로 유의하였다.`;
        } else if (paper.type === 'ttest') {
            let a = paper.levels[0];
            let b = paper.levels[1];
            this.manuscriptSentence = `${assumption}Welch의 독립표본 t-검정을 실시한 결과, ${paper.group}에 따른 ${paper.outcome}의 평균 차이는 ${this.paperSignificancePhrase(paper.p)} (${a.label}: M = ${this.formatStatPaper(a.mean)}, SD = ${this.formatStatPaper(a.sd)}, n = ${this.formatNumber(a.n, 0)}; ${b.label}: M = ${this.formatStatPaper(b.mean)}, SD = ${this.formatStatPaper(b.sd)}, n = ${this.formatNumber(b.n, 0)}; t(${this.formatDfPaper(paper.df)}) = ${this.formatStatPaper(paper.t)}, ${this.formatPPaper(paper.p)}, Cohen's d = ${this.formatStatPaper(paper.d)}).`;
        } else if (paper.type === 'multi_ttest') {
            let top = paper.top;
            let a = top.levels[0];
            let b = top.levels[1];
            let significantCount = paper.tests.filter((item: any) => item.p < this.modelOptions.alpha).length;
            this.manuscriptSentence = `${assumption}선택된 ${this.formatNumber(paper.tests.length, 0)}개 결과-집단 조합에 대해 Welch의 독립표본 t-검정을 실시하였다. 가장 두드러진 차이는 ${top.group}에 따른 ${top.outcome}에서 관찰되었으며 (${a.label}: M = ${this.formatStatPaper(a.mean)}, SD = ${this.formatStatPaper(a.sd)}; ${b.label}: M = ${this.formatStatPaper(b.mean)}, SD = ${this.formatStatPaper(b.sd)}; t(${this.formatDfPaper(top.df)}) = ${this.formatStatPaper(top.t)}, ${this.formatPPaper(top.p)}, Cohen's d = ${this.formatStatPaper(top.d)}), 총 ${this.formatNumber(significantCount, 0)}개 조합이 통계적으로 유의하였다.`;
        } else if (paper.type === 'anova') {
            this.manuscriptSentence = `${assumption}일원분산분석을 실시한 결과, ${paper.group}에 따른 ${paper.outcome}의 평균 차이는 ${this.paperSignificancePhrase(paper.p)} (F(${this.formatDfPaper(paper.dfBetween)}, ${this.formatDfPaper(paper.dfWithin)}) = ${this.formatStatPaper(paper.f)}, ${this.formatPPaper(paper.p)}, η² = ${this.formatStatPaper(paper.etaSquared)}).`;
        } else if (paper.type === 'multi_anova') {
            let top = paper.top;
            let significantCount = paper.tests.filter((item: any) => item.p < this.modelOptions.alpha).length;
            this.manuscriptSentence = `${assumption}선택된 ${this.formatNumber(paper.tests.length, 0)}개 결과-집단 조합에 대해 일원분산분석을 실시하였다. 가장 두드러진 집단 차이는 ${top.group}에 따른 ${top.outcome}에서 관찰되었으며 (F(${this.formatDfPaper(top.dfBetween)}, ${this.formatDfPaper(top.dfWithin)}) = ${this.formatStatPaper(top.f)}, ${this.formatPPaper(top.p)}, η² = ${this.formatStatPaper(top.etaSquared)}), 총 ${this.formatNumber(significantCount, 0)}개 조합이 통계적으로 유의하였다.`;
        } else if (paper.type === 'chisquare') {
            this.manuscriptSentence = `카이제곱 독립성 검정 결과, ${paper.rowName}와 ${paper.columnName} 간 연관성은 ${this.paperSignificancePhrase(paper.p)} (χ²(${this.formatDfPaper(paper.df)}, N = ${this.formatNumber(paper.n, 0)}) = ${this.formatStatPaper(paper.chi)}, ${this.formatPPaper(paper.p)}, Cramer's V = ${this.formatStatPaper(paper.cramer)}).`;
        } else if (paper.type === 'linear') {
            let phrase = paper.p < this.modelOptions.alpha ? '유의하게 예측하였다' : '유의하게 예측하지 않았다';
            this.manuscriptSentence = `${assumption}단순선형회귀분석 결과, ${paper.xName}는 ${paper.yName}를 ${phrase} (B = ${this.formatStatPaper(paper.slope)}, SE = ${this.formatStatPaper(paper.seSlope)}, t(${this.formatDfPaper(paper.df)}) = ${this.formatStatPaper(paper.t)}, ${this.formatPPaper(paper.p)}, R² = ${this.formatStatPaper(paper.r2)}).`;
        } else if (paper.type === 'multi_linear') {
            let top = paper.top;
            let significantCount = paper.tests.filter((item: any) => item.p < this.modelOptions.alpha).length;
            this.manuscriptSentence = `${assumption}선택된 ${this.formatNumber(paper.tests.length, 0)}개 결과-예측 변수 조합에 대해 단순선형회귀분석을 실시하였다. 설명력이 가장 큰 모형은 ${top.xName}로 ${top.yName}를 예측한 모형이었으며 (B = ${this.formatStatPaper(top.slope)}, SE = ${this.formatStatPaper(top.seSlope)}, t(${this.formatDfPaper(top.df)}) = ${this.formatStatPaper(top.t)}, ${this.formatPPaper(top.p)}, R² = ${this.formatStatPaper(top.r2)}), 총 ${this.formatNumber(significantCount, 0)}개 모형이 통계적으로 유의하였다.`;
        } else if (paper.type === 'logistic') {
            let phrase = paper.p < this.modelOptions.alpha ? '유의하게 관련되었다' : '유의한 관련을 보이지 않았다';
            this.manuscriptSentence = `단순 로지스틱 회귀분석 결과, ${paper.xName}의 1 표준편차 증가는 ${paper.positive} 발생 odds와 ${phrase} (OR = ${this.formatStatPaper(paper.oddsRatio)}, B = ${this.formatStatPaper(paper.b1)}, SE = ${this.formatStatPaper(paper.seB1)}, z = ${this.formatStatPaper(paper.zScore)}, ${this.formatPPaper(paper.p)}).`;
        } else {
            this.manuscriptSentence = this.result.conclusion;
        }
    }

    private paperMethodName() {
        if (this.activeAnalysis === 'ttest') return 'Welch 독립표본 t-test';
        if (this.activeAnalysis === 'anova') return '일원분산분석';
        if (this.activeAnalysis === 'correlation') return 'Pearson 상관분석';
        if (this.activeAnalysis === 'linear') return '단순 선형회귀분석';
        if (this.activeAnalysis === 'chisquare') return '카이제곱 독립성 검정';
        if (this.activeAnalysis === 'logistic') return '단순 로지스틱 회귀분석';
        return '기술통계 및 빈도분석';
    }

    private paperFigureLabels(kind: string) {
        let paper = this.result?.paper || {};
        let annotation = this.paperFigureAnnotation();
        if (kind === 'box_dot') {
            return {
                paperTitle: 'Figure. Distribution of the primary outcome by study group',
                xLabel: 'Study group',
                yLabel: 'Primary outcome',
                annotation
            };
        }
        if (kind === 'mean_ci') {
            return {
                paperTitle: 'Figure. Mean comparison with error bars',
                xLabel: 'Study group',
                yLabel: 'Mean ± SE',
                annotation
            };
        }
        if (kind === 'effect_forest') {
            return {
                paperTitle: 'Figure. Standardized effect sizes across outcomes',
                xLabel: 'Effect size with 95% CI',
                yLabel: 'Outcome variable',
                annotation
            };
        }
        if (kind === 'correlation_heatmap') {
            return {
                paperTitle: 'Figure. Correlation matrix of study variables',
                xLabel: 'Variable',
                yLabel: 'Variable',
                annotation
            };
        }
        if (kind === 'scatter_fit' || kind === 'scatter') {
            return {
                paperTitle: paper.type === 'linear' ? 'Figure. Linear association between predictor and outcome' : 'Figure. Association between predictor and outcome',
                xLabel: 'Predictor variable',
                yLabel: 'Outcome variable',
                annotation
            };
        }
        if (kind === 'residual') {
            return {
                paperTitle: 'Figure. Residual diagnostics for the fitted model',
                xLabel: 'Fitted value',
                yLabel: 'Residual',
                annotation
            };
        }
        if (kind === 'stacked_bar' || kind === 'rate_bar') {
            return {
                paperTitle: 'Figure. Distribution of outcome categories by comparison group',
                xLabel: 'Comparison group',
                yLabel: kind === 'rate_bar' ? 'Proportion (%)' : 'Frequency',
                annotation
            };
        }
        if (kind === 'logistic_curve' || kind === 'binary_scatter') {
            return {
                paperTitle: 'Figure. Predicted probability of the target outcome',
                xLabel: 'Predictor variable',
                yLabel: 'Predicted probability',
                annotation
            };
        }
        if (kind === 'missing') {
            return {
                paperTitle: 'Figure. Missing data pattern by variable',
                xLabel: 'Variable',
                yLabel: 'Missing observations',
                annotation: 'Missing data summary'
            };
        }
        return {
            paperTitle: 'Figure. Descriptive summary of study variables',
            xLabel: 'Study variable',
            yLabel: 'Mean ± SD',
            annotation: this.result?.pValue === undefined ? 'Descriptive statistics' : annotation
        };
    }

    private paperFigureAnnotation() {
        let paper = this.result?.paper;
        if (!paper) return this.result?.pValue === undefined ? 'Descriptive statistics' : this.formatPWithPaperStars(this.result.pValue);
        if (paper.type === 'ttest') return `Welch t(${this.formatDfPaper(paper.df)}) = ${this.formatStatPaper(paper.t)}, ${this.formatPWithPaperStars(paper.p)}`;
        if (paper.type === 'multi_ttest') return `Top result: Welch t(${this.formatDfPaper(paper.top.df)}) = ${this.formatStatPaper(paper.top.t)}, ${this.formatPWithPaperStars(paper.top.p)}`;
        if (paper.type === 'anova') return `F(${this.formatDfPaper(paper.dfBetween)}, ${this.formatDfPaper(paper.dfWithin)}) = ${this.formatStatPaper(paper.f)}, ${this.formatPWithPaperStars(paper.p)}`;
        if (paper.type === 'multi_anova') return `Top result: F(${this.formatDfPaper(paper.top.dfBetween)}, ${this.formatDfPaper(paper.top.dfWithin)}) = ${this.formatStatPaper(paper.top.f)}, ${this.formatPWithPaperStars(paper.top.p)}`;
        if (paper.type === 'correlation') return `Pearson r = ${this.formatStatPaper(paper.r)}, ${this.formatPWithPaperStars(paper.p)}`;
        if (paper.type === 'multi_correlation') return `Strongest pair: r = ${this.formatStatPaper(paper.top.r)}, ${this.formatPWithPaperStars(paper.top.p)}`;
        if (paper.type === 'linear') return `B = ${this.formatStatPaper(paper.slope)}, R² = ${this.formatStatPaper(paper.r2)}, ${this.formatPWithPaperStars(paper.p)}`;
        if (paper.type === 'multi_linear') return `Best model: B = ${this.formatStatPaper(paper.top.slope)}, R² = ${this.formatStatPaper(paper.top.r2)}, ${this.formatPWithPaperStars(paper.top.p)}`;
        if (paper.type === 'chisquare') return `χ²(${this.formatDfPaper(paper.df)}) = ${this.formatStatPaper(paper.chi)}, ${this.formatPWithPaperStars(paper.p)}`;
        if (paper.type === 'logistic') return `OR = ${this.formatStatPaper(paper.oddsRatio)}, ${this.formatPWithPaperStars(paper.p)}`;
        return 'Descriptive statistics';
    }

    private buildGraphPreview(previewType: string) {
        if (previewType === 'bar_error' || previewType === 'box' || previewType === 'box_dot') {
            return this.buildGroupedGraphPreview(previewType);
        }
        if (previewType === 'scatter' || previewType === 'scatter_fit') {
            return this.buildScatterGraphPreview(previewType);
        }
        if (previewType === 'residual') return this.buildResidualGraphPreview();
        if (previewType === 'binary_scatter' || previewType === 'logistic_curve') {
            return this.buildBinaryGraphPreview(previewType);
        }
        if (previewType === 'stacked_bar') return this.buildStackedGraphPreview();
        if (previewType === 'heatmap') return this.buildHeatmapGraphPreview();
        if (previewType === 'forest') return this.buildForestGraphPreview();
        if (previewType === 'lollipop') return this.buildLollipopGraphPreview();
        return this.buildGroupedGraphPreview('bar_error');
    }

    private buildGroupedGraphPreview(kind: string) {
        let source: any[] = [];
        let groupName = this.group || this.predictor;
        if (this.outcome && groupName) source = this.groupNumericValues(this.outcome, groupName);

        if (source.length === 0) {
            let selected = this.selectedOutcomes.filter((name: string) => this.numericColumns.some((column: ColumnProfile) => column.name === name));
            let names = (selected.length > 0 ? selected : this.numericColumns.map((column: ColumnProfile) => column.name)).slice(0, 5);
            source = names.map((name: string) => ({
                level: name,
                values: this.rows.map((row: any) => this.numericValue(row, name)).filter((value: number | null) => value !== null)
            }));
        }

        source = source.filter((item: any) => item.values.length > 0).slice(0, 5);
        let allValues: number[] = [];
        source.forEach((item: any) => allValues.push(...item.values));
        let range = this.previewRange(allValues, kind === 'bar_error');
        let slot = source.length > 0 ? 510 / source.length : 510;
        let visibleCount = 0;
        let totalCount = allValues.length;
        let colors = ['#4f46e5', '#059669', '#e11d48', '#d97706', '#0284c7'];
        let fills = ['#c7d2fe', '#a7f3d0', '#fecdd3', '#fde68a', '#bae6fd'];

        let groups = source.map((item: any, groupIndex: number) => {
            let sorted = item.values.slice().sort((a: number, b: number) => a - b);
            let q1 = this.quantile(sorted, 0.25);
            let median = this.quantile(sorted, 0.5);
            let q3 = this.quantile(sorted, 0.75);
            let iqr = q3 - q1;
            let lowerFence = q1 - 1.5 * iqr;
            let upperFence = q3 + 1.5 * iqr;
            let regular = sorted.filter((value: number) => value >= lowerFence && value <= upperFence);
            let whiskerLow = regular.length > 0 ? regular[0] : sorted[0];
            let whiskerHigh = regular.length > 0 ? regular[regular.length - 1] : sorted[sorted.length - 1];
            let mean = this.mean(sorted);
            let se = this.standardDeviation(sorted) / Math.sqrt(Math.max(1, sorted.length));
            let x = 95 + slot * (groupIndex + 0.5);
            let barWidth = Math.min(78, slot * 0.5);
            let sampled = this.previewSample(sorted, Math.max(24, Math.floor(160 / Math.max(1, source.length))));
            let points = sampled.map((value: number, pointIndex: number) => ({
                x: x + this.previewJitter(pointIndex, Math.min(32, slot * 0.3)),
                y: this.previewY(value, range),
                label: `${item.level}: ${this.formatNumber(value, 2)}`,
                color: colors[groupIndex % colors.length],
                outlier: value < lowerFence || value > upperFence
            }));
            visibleCount += points.length;
            let zeroY = this.previewY(0, range);
            let meanY = this.previewY(mean, range);
            return {
                label: this.previewShortLabel(item.level),
                fullLabel: item.level,
                n: sorted.length,
                x,
                barWidth,
                color: colors[groupIndex % colors.length],
                fill: fills[groupIndex % fills.length],
                points,
                meanY,
                zeroY,
                barY: Math.min(meanY, zeroY),
                barHeight: Math.max(1, Math.abs(zeroY - meanY)),
                errorTopY: this.previewClampY(this.previewY(mean + se, range)),
                errorBottomY: this.previewClampY(this.previewY(mean - se, range)),
                boxTopY: this.previewY(q3, range),
                boxBottomY: this.previewY(q1, range),
                boxHeight: Math.max(3, this.previewY(q1, range) - this.previewY(q3, range)),
                medianY: this.previewY(median, range),
                whiskerTopY: this.previewY(whiskerHigh, range),
                whiskerBottomY: this.previewY(whiskerLow, range)
            };
        });

        return {
            kind,
            groups,
            points: [],
            yTicks: this.previewTicks(range),
            xTicks: [],
            totalCount,
            visibleCount,
            sampled: visibleCount < totalCount,
            xLabel: groupName || 'Study variable',
            yLabel: this.outcome || 'Value'
        };
    }

    private buildScatterGraphPreview(kind: string) {
        let xName = this.predictor || this.selectedPredictors[0] || this.numericColumns[0]?.name || '';
        let yName = this.outcome || this.selectedOutcomes[0] || this.numericColumns.find((column: ColumnProfile) => column.name !== xName)?.name || '';
        let pairs = xName && yName ? this.numericPairs(xName, yName) : [];
        let xRange = this.previewRange(pairs.map((pair: any) => pair.x));
        let yRange = this.previewRange(pairs.map((pair: any) => pair.y));
        let sampled = this.previewSample(pairs, 180);
        let points = sampled.map((pair: any) => ({
            x: this.previewX(pair.x, xRange),
            y: this.previewY(pair.y, yRange),
            label: `${xName} ${this.formatNumber(pair.x, 2)} · ${yName} ${this.formatNumber(pair.y, 2)}`,
            color: '#4f46e5'
        }));
        let xs = pairs.map((pair: any) => pair.x);
        let ys = pairs.map((pair: any) => pair.y);
        let xMean = this.mean(xs);
        let yMean = this.mean(ys);
        let denominator = xs.reduce((sum: number, value: number) => sum + Math.pow(value - xMean, 2), 0);
        let slope = denominator === 0 ? 0 : xs.reduce((sum: number, value: number, index: number) => sum + (value - xMean) * (ys[index] - yMean), 0) / denominator;
        let intercept = yMean - slope * xMean;
        let trend = {
            x1: this.previewX(xRange.min, xRange),
            y1: this.previewClampY(this.previewY(intercept + slope * xRange.min, yRange)),
            x2: this.previewX(xRange.max, xRange),
            y2: this.previewClampY(this.previewY(intercept + slope * xRange.max, yRange))
        };
        return {
            kind,
            groups: [],
            points,
            trend,
            xTicks: this.previewTicks(xRange, true),
            yTicks: this.previewTicks(yRange),
            totalCount: pairs.length,
            visibleCount: points.length,
            sampled: points.length < pairs.length,
            xLabel: xName,
            yLabel: yName
        };
    }

    private buildResidualGraphPreview() {
        let xName = this.predictor || this.selectedPredictors[0] || this.numericColumns[0]?.name || '';
        let yName = this.outcome || this.selectedOutcomes[0] || this.numericColumns.find((column: ColumnProfile) => column.name !== xName)?.name || '';
        let pairs = xName && yName ? this.numericPairs(xName, yName) : [];
        let xs = pairs.map((pair: any) => pair.x);
        let ys = pairs.map((pair: any) => pair.y);
        let xMean = this.mean(xs);
        let yMean = this.mean(ys);
        let denominator = xs.reduce((sum: number, value: number) => sum + Math.pow(value - xMean, 2), 0);
        let slope = denominator === 0 ? 0 : xs.reduce((sum: number, value: number, index: number) => sum + (value - xMean) * (ys[index] - yMean), 0) / denominator;
        let intercept = yMean - slope * xMean;
        let residuals = pairs.map((pair: any) => ({ x: pair.x, residual: pair.y - (intercept + slope * pair.x) }));
        let xRange = this.previewRange(xs);
        let maxResidual = Math.max(1e-6, ...residuals.map((item: any) => Math.abs(item.residual)));
        let yRange = { min: -maxResidual * 1.15, max: maxResidual * 1.15 };
        let sampled = this.previewSample(residuals, 180);
        let points = sampled.map((item: any) => ({
            x: this.previewX(item.x, xRange),
            y: this.previewY(item.residual, yRange),
            label: `${xName} ${this.formatNumber(item.x, 2)} · 잔차 ${this.formatNumber(item.residual, 2)}`,
            color: item.residual >= 0 ? '#4f46e5' : '#e11d48'
        }));
        return {
            kind: 'residual',
            groups: [],
            points,
            zeroY: this.previewY(0, yRange),
            xTicks: this.previewTicks(xRange, true),
            yTicks: this.previewTicks(yRange),
            totalCount: residuals.length,
            visibleCount: points.length,
            sampled: points.length < residuals.length,
            xLabel: xName,
            yLabel: 'Residual'
        };
    }

    private buildBinaryGraphPreview(kind: string) {
        let xName = this.predictor || this.selectedPredictors[0] || this.numericColumns[0]?.name || '';
        let yName = this.outcome || this.selectedOutcomes[0] || this.categoricalColumns.find((column: ColumnProfile) => column.distinct === 2)?.name || '';
        let levels = Array.from(new Set(this.rows.map((row: any) => this.categoryValue(row, yName)).filter((value: string) => value)));
        let positive = levels.indexOf(this.positiveLevel) >= 0 ? this.positiveLevel : levels[levels.length - 1];
        let reference = levels.find((level: string) => level !== positive) || '0';
        let pairs = this.rows.map((row: any) => {
            let x = this.numericValue(row, xName);
            let raw = this.categoryValue(row, yName);
            if (x === null || !raw) return null;
            return { x, y: raw === positive ? 1 : 0, raw };
        }).filter((item: any) => item !== null);
        let xRange = this.previewRange(pairs.map((pair: any) => pair.x));
        let sampled = this.previewSample(pairs, 180);
        let points = sampled.map((pair: any, index: number) => ({
            x: this.previewX(pair.x, xRange),
            y: (pair.y === 1 ? 87 : 255) + this.previewJitter(index, 9),
            label: `${xName} ${this.formatNumber(pair.x, 2)} · ${yName} ${pair.raw}`,
            color: pair.y === 1 ? '#4f46e5' : '#94a3b8'
        }));

        let curvePath = '';
        if (pairs.length > 1) {
            let xs = pairs.map((pair: any) => pair.x);
            let ys = pairs.map((pair: any) => pair.y);
            let xMean = this.mean(xs);
            let xSd = this.standardDeviation(xs) || 1;
            let positiveRate = (ys.reduce((sum: number, value: number) => sum + value, 0) + 0.5) / (ys.length + 1);
            let b0 = Math.log(positiveRate / (1 - positiveRate));
            let b1 = 0;
            for (let step = 0; step < 400; step++) {
                let gradient0 = 0;
                let gradient1 = 0;
                pairs.forEach((pair: any) => {
                    let z = (pair.x - xMean) / xSd;
                    let probability = this.sigmoid(b0 + b1 * z);
                    gradient0 += pair.y - probability;
                    gradient1 += (pair.y - probability) * z;
                });
                b0 += 0.08 * gradient0 / pairs.length;
                b1 += 0.08 * gradient1 / pairs.length;
            }
            let curve: string[] = [];
            for (let index = 0; index <= 36; index++) {
                let value = xRange.min + (xRange.max - xRange.min) * index / 36;
                let probability = this.sigmoid(b0 + b1 * ((value - xMean) / xSd));
                let x = this.previewX(value, xRange);
                let y = 255 - probability * 168;
                curve.push(`${index === 0 ? 'M' : 'L'}${this.formatNumber(x, 1)} ${this.formatNumber(y, 1)}`);
            }
            curvePath = curve.join(' ');
        }

        return {
            kind,
            groups: [],
            points,
            curvePath,
            xTicks: this.previewTicks(xRange, true),
            yTicks: [{ y: 87, label: this.previewShortLabel(positive, 10) }, { y: 255, label: this.previewShortLabel(reference, 10) }],
            totalCount: pairs.length,
            visibleCount: points.length,
            sampled: points.length < pairs.length,
            xLabel: xName,
            yLabel: yName
        };
    }

    private buildStackedGraphPreview() {
        let outcomeName = this.outcome || this.selectedOutcomes[0] || '';
        let groupName = this.predictor || this.group || this.selectedPredictors[0] || '';
        let pairs = outcomeName && groupName ? this.categoryPairs(outcomeName, groupName) : [];
        let groupLevels = Array.from(new Set(pairs.map((pair: any) => pair.column))).slice(0, 5);
        let categoryLevels = Array.from(new Set(pairs.map((pair: any) => pair.row))).slice(0, 5);
        let colors = ['#4f46e5', '#10b981', '#f59e0b', '#f43f5e', '#0ea5e9'];
        let slot = groupLevels.length > 0 ? 510 / groupLevels.length : 510;
        let visibleCount = 0;
        let groups = groupLevels.map((level: string, groupIndex: number) => {
            let groupPairs = pairs.filter((pair: any) => pair.column === level && categoryLevels.indexOf(pair.row) >= 0);
            let x = 95 + slot * (groupIndex + 0.5);
            let barWidth = Math.min(84, slot * 0.52);
            let cursor = 282;
            let segments = categoryLevels.map((category: string, categoryIndex: number) => {
                let count = groupPairs.filter((pair: any) => pair.row === category).length;
                let height = groupPairs.length > 0 ? 210 * count / groupPairs.length : 0;
                cursor -= height;
                let segment = { category, count, y: cursor, height, color: colors[categoryIndex % colors.length] };
                return segment;
            });
            let sampled = this.previewSample(groupPairs, Math.max(24, Math.floor(150 / Math.max(1, groupLevels.length))));
            let categoryOffsets: any = {};
            let points = sampled.map((pair: any) => {
                let segment = segments.find((item: any) => item.category === pair.row);
                let index = categoryOffsets[pair.row] || 0;
                categoryOffsets[pair.row] = index + 1;
                let denominator = Math.max(1, Math.min(segment.count, sampled.filter((item: any) => item.row === pair.row).length));
                return {
                    x: x + this.previewJitter(index, Math.min(30, barWidth * 0.42)),
                    y: segment.y + segment.height * (index + 0.5) / denominator,
                    label: `${level} · ${pair.row}`,
                    color: segment.color
                };
            });
            visibleCount += points.length;
            return { label: this.previewShortLabel(level), fullLabel: level, x, barWidth, segments, points, n: groupPairs.length };
        });
        return {
            kind: 'stacked_bar',
            groups,
            categories: categoryLevels.map((label: string, index: number) => ({ label: this.previewShortLabel(label, 12), color: colors[index % colors.length] })),
            points: [],
            xTicks: [],
            yTicks: [{ y: 72, label: '100%' }, { y: 177, label: '50%' }, { y: 282, label: '0%' }],
            totalCount: pairs.length,
            visibleCount,
            sampled: visibleCount < pairs.length,
            xLabel: groupName,
            yLabel: 'Proportion'
        };
    }

    private buildHeatmapGraphPreview() {
        let candidates = [...this.selectedOutcomes, ...this.selectedPredictors, ...this.numericColumns.map((column: ColumnProfile) => column.name)];
        let names = Array.from(new Set(candidates.filter((name: string) => this.numericColumns.some((column: ColumnProfile) => column.name === name)))).slice(0, 5);
        let cellSize = Math.min(58, 220 / Math.max(1, names.length));
        let left = 225;
        let top = 70;
        let cells: any[] = [];
        names.forEach((rowName: string, rowIndex: number) => {
            names.forEach((columnName: string, columnIndex: number) => {
                let pairs = this.numericPairs(rowName, columnName);
                let value = rowName === columnName ? 1 : this.correlation(pairs.map((pair: any) => pair.x), pairs.map((pair: any) => pair.y));
                let opacity = 0.14 + Math.abs(value) * 0.72;
                cells.push({
                    x: left + columnIndex * cellSize,
                    y: top + rowIndex * cellSize,
                    width: cellSize - 3,
                    height: cellSize - 3,
                    value: this.formatNumber(value, 2),
                    fill: value >= 0 ? `rgba(79, 70, 229, ${opacity})` : `rgba(244, 63, 94, ${opacity})`,
                    textColor: Math.abs(value) > 0.55 ? '#ffffff' : '#334155'
                });
            });
        });
        return {
            kind: 'heatmap',
            groups: [],
            points: [],
            cells,
            labels: names.map((name: string, index: number) => ({ label: this.previewShortLabel(name, 11), x: left + index * cellSize + (cellSize - 3) / 2, y: top + index * cellSize + (cellSize - 3) / 2 })),
            totalCount: 0,
            visibleCount: 0,
            sampled: false,
            xLabel: 'Variables',
            yLabel: 'Variables'
        };
    }

    private buildForestGraphPreview() {
        let names = this.selectedOutcomes.filter((name: string) => this.numericColumns.some((column: ColumnProfile) => column.name === name));
        if (names.length === 0) names = this.numericColumns.map((column: ColumnProfile) => column.name);
        names = names.slice(0, 6);
        let groupName = this.group || this.predictor;
        let predictorName = this.predictor || this.selectedPredictors[0];
        let rawItems = names.map((name: string) => {
            let effect = 0;
            let se = 0.25;
            let grouped = groupName ? this.groupNumericValues(name, groupName) : [];
            if (grouped.length >= 2) {
                let first = grouped[0].values;
                let second = grouped[1].values;
                let pooled = Math.sqrt((this.variance(first) + this.variance(second)) / 2) || 1;
                effect = (this.mean(second) - this.mean(first)) / pooled;
                se = Math.sqrt(1 / Math.max(1, first.length) + 1 / Math.max(1, second.length));
            } else if (predictorName && predictorName !== name) {
                let pairs = this.numericPairs(predictorName, name);
                effect = this.correlation(pairs.map((pair: any) => pair.x), pairs.map((pair: any) => pair.y));
                se = 1 / Math.sqrt(Math.max(3, pairs.length));
            }
            return { label: this.previewShortLabel(name, 16), effect, low: effect - 1.96 * se, high: effect + 1.96 * se };
        });
        let extent = Math.max(1, ...rawItems.map((item: any) => Math.max(Math.abs(item.low), Math.abs(item.high)))) * 1.12;
        let scale = (value: number) => 365 + value / extent * 205;
        let items = rawItems.map((item: any, index: number) => ({ ...item, effectLabel: this.formatNumber(item.effect, 2), x: scale(item.effect), lowX: scale(item.low), highX: scale(item.high), y: 88 + index * 34 }));
        return {
            kind: 'forest',
            groups: [],
            points: [],
            items,
            zeroX: scale(0),
            totalCount: 0,
            visibleCount: 0,
            sampled: false,
            xLabel: 'Standardized effect (95% CI)',
            yLabel: 'Outcome'
        };
    }

    private buildLollipopGraphPreview() {
        let columns = this.columns.slice().sort((a: ColumnProfile, b: ColumnProfile) => b.missing - a.missing).slice(0, 6);
        let maxMissing = Math.max(1, ...columns.map((column: ColumnProfile) => column.missing));
        let items = columns.map((column: ColumnProfile, index: number) => ({
            label: this.previewShortLabel(column.name, 16),
            value: column.missing,
            x: 195 + column.missing / maxMissing * 390,
            y: 82 + index * 35
        }));
        return {
            kind: 'lollipop',
            groups: [],
            points: [],
            items,
            totalCount: 0,
            visibleCount: 0,
            sampled: false,
            xLabel: 'Missing observations',
            yLabel: 'Variable'
        };
    }

    private previewRange(values: number[], includeZero: boolean = false) {
        let finite = values.filter((value: number) => isFinite(value));
        if (finite.length === 0) return { min: 0, max: 1 };
        let min = Math.min(...finite);
        let max = Math.max(...finite);
        if (includeZero) {
            min = Math.min(0, min);
            max = Math.max(0, max);
        }
        if (min === max) {
            let fallback = Math.abs(min) * 0.1 || 1;
            min -= fallback;
            max += fallback;
        }
        let padding = (max - min) * 0.08;
        if (!(includeZero && min === 0)) min -= padding;
        if (!(includeZero && max === 0)) max += padding;
        return { min, max };
    }

    private previewX(value: number, range: any) {
        return 92 + (value - range.min) / (range.max - range.min) * 510;
    }

    private previewY(value: number, range: any) {
        return 282 - (value - range.min) / (range.max - range.min) * 210;
    }

    private previewClampY(value: number) {
        return Math.max(66, Math.min(282, value));
    }

    private previewTicks(range: any, horizontal: boolean = false) {
        let ticks: any[] = [];
        for (let index = 0; index < 5; index++) {
            let ratio = index / 4;
            let value = range.min + (range.max - range.min) * ratio;
            let magnitude = Math.max(Math.abs(range.min), Math.abs(range.max));
            let digits = magnitude < 1 ? 2 : (magnitude < 100 ? 1 : 0);
            ticks.push({
                value,
                label: this.formatNumber(value, digits),
                x: this.previewX(value, range),
                y: this.previewY(value, range)
            });
        }
        return horizontal ? ticks : ticks.reverse();
    }

    private previewSample(values: any[], limit: number) {
        if (values.length <= limit) return values.slice();
        let sampled: any[] = [];
        let step = values.length / limit;
        for (let index = 0; index < limit; index++) sampled.push(values[Math.floor(index * step)]);
        return sampled;
    }

    private previewJitter(index: number, amplitude: number) {
        let pattern = [0, -0.36, 0.36, -0.68, 0.68, -0.18, 0.18, -0.52, 0.52, -0.84, 0.84];
        let ring = Math.floor(index / pattern.length);
        return pattern[index % pattern.length] * amplitude + ((ring % 3) - 1) * 1.4;
    }

    private previewShortLabel(value: any, maxLength: number = 13) {
        let label = String(value || '-');
        return label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label;
    }

    private buildGraphRecommendations() {
        this.graphRecommendations = [];
        if (!this.result) return;

        let pLabel = this.result.pValue === undefined ? 'p 없음' : `p=${this.formatPWithStars(this.result.pValue)}`;
        let significant = this.result.pValue !== undefined && this.result.pValue < this.modelOptions.alpha;
        let sigReason = this.result.pValue === undefined ? '검정 결과가 없는 요약 그래프입니다.' : (significant ? '유의확률을 그래프 안에 함께 보여줍니다.' : '비유의 결과도 p값과 함께 보여줍니다.');
        let add = (id: string, label: string, kind: string, reason: string, previewType: string, insight: string) => {
            let priority = this.graphRecommendations.length === 0 ? 'AI 필수 그래프' : '보조 추천';
            let paperLabels = this.paperFigureLabels(kind);
            let preview = this.buildGraphPreview(previewType);
            this.graphRecommendations.push({ id, label, kind, reason, previewType, priority, insight, significant, pLabel, preview, ...paperLabels });
        };

        if (this.activeAnalysis === 'ttest') {
            let insight = this.groupDifferenceInsight(this.outcome, this.group || this.predictor);
            add('mean-ci', '평균 + 에러바 + 개별점', 'mean_ci', `${this.group}별 ${this.outcome} 평균을 Mean ± SE로 표시하고 실제 관측치를 함께 겹칩니다. ${sigReason}`, 'bar_error', insight);
            add('box-dot', '박스차트 + 개별점', 'box_dot', `분포, 중앙값, 이상값 후보, 개별 관측치를 함께 보여 논문 보조 피겨로 좋습니다. ${sigReason}`, 'box_dot', insight);
            if (this.result.paper?.type === 'multi_ttest') add('effect-forest', '효과크기 forest plot', 'effect_forest', '여러 결과변수의 효과크기를 한 장에서 비교합니다. purity와 recovery rate를 동시에 보여줄 때 적합합니다.', 'forest', '0선을 기준으로 어느 지표가 더 크게 개선됐는지 비교합니다.');
        } else if (this.activeAnalysis === 'anova') {
            let insight = this.groupDifferenceInsight(this.outcome, this.group || this.predictor);
            add('anova-mean', '평균 + 에러바 + 개별점', 'mean_ci', `${this.group}의 여러 수준별 평균과 실제 관측치를 Mean ± SE 위에 함께 표시합니다. ${sigReason}`, 'bar_error', insight);
            add('anova-box', '박스차트 + 개별점', 'box_dot', `집단별 산포, 중앙값, 이상값 후보를 함께 봅니다. ${sigReason}`, 'box_dot', insight);
            if (this.result.paper?.type === 'multi_anova') add('anova-forest', '효과크기 forest plot', 'effect_forest', '여러 결과변수의 η² 또는 표준화 효과를 한 장에 요약합니다.', 'forest', '유의한 지표와 효과가 큰 지표를 한 번에 고를 수 있습니다.');
        } else if (this.activeAnalysis === 'correlation') {
            let insight = this.correlationInsight(this.predictor, this.outcome);
            if (this.result.paper?.type === 'multi_correlation') add('corr-heatmap', '상관행렬 heatmap', 'correlation_heatmap', '여러 변수 사이의 상관 구조를 논문용 요약 피겨로 보여줍니다.', 'heatmap', '색이 진할수록 상관이 크며, 유의한 셀에는 별표를 표시합니다.');
            add('corr-scatter', '산점도 + 회귀선', 'scatter_fit', `${this.predictor}와 ${this.outcome}의 방향, 강도, ${pLabel}를 한 번에 봐야 합니다.`, 'scatter_fit', insight);
            add('corr-label', '산점도 + 신뢰대역', 'scatter', `관계가 일부 관측치에 의해 만들어졌는지 확인합니다. ${sigReason}`, 'scatter', insight);
        } else if (this.activeAnalysis === 'linear') {
            let insight = this.correlationInsight(this.predictor, this.outcome);
            if (this.result.paper?.type === 'multi_linear') add('coef-forest', '회귀계수 forest plot', 'effect_forest', '여러 예측 변수 후보의 계수와 신뢰구간을 한 장에서 비교합니다.', 'forest', '0선을 지나지 않는 계수가 논문에서 강조할 후보입니다.');
            add('reg-fit', '산점도 + 회귀선', 'scatter_fit', `회귀선과 ${pLabel}를 함께 표시해 설명력을 확인합니다.`, 'scatter_fit', insight);
            add('reg-residual', '잔차 진단 그래프', 'residual', `예측값 대비 잔차 패턴을 확인해 선형 모델이 무리 없는지 봅니다.`, 'residual', '잔차가 한쪽으로 몰리면 비선형 또는 영향점 검토가 필요합니다.');
        } else if (this.activeAnalysis === 'chisquare') {
            let insight = this.categoryAssociationInsight(this.outcome, this.predictor);
            add('chi-bar', '누적 막대 + 관측점', 'stacked_bar', `${this.outcome} × ${this.predictor} 교차 빈도와 각 범주 관측치를 함께 표시합니다. ${pLabel}`, 'stacked_bar', insight);
            add('chi-rate', '비율 막대 + 관측점', 'rate_bar', `범주 비율과 실제 관측치 분포를 함께 확인합니다. ${sigReason}`, 'stacked_bar', insight);
        } else if (this.activeAnalysis === 'logistic') {
            let insight = this.logisticTrendInsight(this.outcome, this.predictor);
            add('logit-prob', '예측확률 곡선', 'logistic_curve', `${this.predictor}에 따른 ${this.positiveLevel || this.outcome} 확률 변화가 핵심입니다.`, 'logistic_curve', insight);
            add('logit-jitter', '이진 결과 jitter plot', 'binary_scatter', `이진 결과가 예측 변수 구간별로 어떻게 갈리는지 확인합니다. ${sigReason}`, 'binary_scatter', insight);
        } else {
            add('summary-numeric', '평균 + 표준편차 + 개별점', 'summary', '숫자형 변수의 평균과 표준편차 위에 실제 관측치를 겹쳐 분포를 함께 보여줍니다.', 'bar_error', this.strongestCorrelationInsight());
            add('missing-profile', '결측 현황 lollipop plot', 'missing', '변수별 결측 개수를 확인해 분석 제외 위험을 봅니다.', 'lollipop', '결측이 많은 변수는 모델 선택과 해석에서 별도 확인이 필요합니다.');
        }

        if (this.graphRecommendations.length > 0 && !this.graphRecommendations.some((item: GraphRecommendation) => item.id === this.selectedGraphId)) {
            this.selectedGraphId = this.graphRecommendations[0].id;
        }
    }

    private requirement(title: string, message: string): AnalysisResult {
        return {
            title,
            subtitle: '모델 설정 확인',
            metrics: [
                { label: '상태', value: '설정 필요', accent: 'text-amber-700' },
                { label: '행 수', value: this.formatNumber(this.rows.length, 0), accent: 'text-gray-900' }
            ],
            tables: [],
            notes: [message],
            conclusion: message,
            status: 'warning'
        };
    }

    private hasColumn(name: string) {
        return this.columns.some((column: ColumnProfile) => column.name === name);
    }

    private requireNumeric(name: string, exclude: string[] = []) {
        if (name && this.numericColumns.some((column: ColumnProfile) => column.name === name) && exclude.indexOf(name) < 0) return name;
        let found = this.numericColumns.find((column: ColumnProfile) => exclude.indexOf(column.name) < 0);
        return found ? found.name : '';
    }

    private requireCategorical(name: string, exclude: string[] = []) {
        if (name && this.categoricalColumns.some((column: ColumnProfile) => column.name === name) && exclude.indexOf(name) < 0) return name;
        let found = this.categoricalColumns.find((column: ColumnProfile) => exclude.indexOf(column.name) < 0);
        return found ? found.name : '';
    }

    private numericPairs(xName: string, yName: string) {
        return this.rows.map((row: any) => {
            let x = this.numericValue(row, xName);
            let y = this.numericValue(row, yName);
            if (x === null || y === null) return null;
            return { x, y };
        }).filter((item: any) => item !== null);
    }

    private categoryPairs(rowName: string, columnName: string) {
        return this.rows.map((row: any) => {
            let rowValue = this.categoryValue(row, rowName);
            let columnValue = this.categoryValue(row, columnName);
            if (!rowValue || !columnValue) return null;
            return { row: rowValue, column: columnValue };
        }).filter((item: any) => item !== null);
    }

    private groupNumericValues(outcome: string, group: string) {
        let grouped: any = {};
        this.rows.forEach((row: any) => {
            let level = this.categoryValue(row, group);
            let value = this.numericValue(row, outcome);
            if (!level || value === null) return;
            if (!grouped[level]) grouped[level] = [];
            grouped[level].push(value);
        });
        return Object.keys(grouped).map((level: string) => ({ level, values: grouped[level] }));
    }

    private numericValue(row: any, column: string) {
        return this.parseNumber(row[column]);
    }

    private categoryValue(row: any, column: string) {
        let value = row[column];
        if (this.isMissing(value)) return '';
        return String(value).trim();
    }

    private cleanValue(value: any) {
        if (value === null || value === undefined) return '';
        return String(value).trim();
    }

    private isMissing(value: any) {
        if (value === null || value === undefined) return true;
        let normalized = String(value).trim().toLowerCase();
        return this.missingTokens.indexOf(normalized) >= 0;
    }

    private parseNumber(value: any): number | null {
        if (this.isMissing(value)) return null;
        let text = String(value).trim();
        let percent = /%$/.test(text);
        text = text.replace(/,/g, '').replace(/%$/g, '').trim();
        if (text === '') return null;
        let parsed = Number(text);
        if (!isFinite(parsed)) return null;
        return percent ? parsed / 100 : parsed;
    }

    private mean(values: number[]) {
        if (!values.length) return 0;
        return values.reduce((sum: number, value: number) => sum + value, 0) / values.length;
    }

    private variance(values: number[]) {
        if (values.length < 2) return 0;
        let avg = this.mean(values);
        return values.reduce((sum: number, value: number) => sum + Math.pow(value - avg, 2), 0) / (values.length - 1);
    }

    private standardDeviation(values: number[]) {
        return Math.sqrt(this.variance(values));
    }

    private quantile(sortedValues: number[], q: number) {
        if (!sortedValues.length) return 0;
        let position = (sortedValues.length - 1) * q;
        let base = Math.floor(position);
        let rest = position - base;
        if (sortedValues[base + 1] !== undefined) {
            return sortedValues[base] + rest * (sortedValues[base + 1] - sortedValues[base]);
        }
        return sortedValues[base];
    }

    private correlation(xs: number[], ys: number[]) {
        let xMean = this.mean(xs);
        let yMean = this.mean(ys);
        let numerator = xs.reduce((sum: number, x: number, index: number) => sum + (x - xMean) * (ys[index] - yMean), 0);
        let xDenominator = xs.reduce((sum: number, x: number) => sum + Math.pow(x - xMean, 2), 0);
        let yDenominator = ys.reduce((sum: number, y: number) => sum + Math.pow(y - yMean, 2), 0);
        let denominator = Math.sqrt(xDenominator * yDenominator);
        return denominator === 0 ? 0 : numerator / denominator;
    }

    private sigmoid(value: number) {
        if (value > 35) return 1;
        if (value < -35) return 0;
        return 1 / (1 + Math.exp(-value));
    }

    private significanceConclusion(p: number, prefix: string) {
        let alpha = this.modelOptions.alpha;
        if (p < alpha) return `${prefix} 유의수준 ${alpha} 기준에서 통계적으로 유의합니다.`;
        return `${prefix} 유의수준 ${alpha} 기준에서 통계적으로 유의하다고 보기 어렵습니다.`;
    }

    private pAccent(p: number) {
        return p < this.modelOptions.alpha ? 'text-emerald-700' : 'text-gray-700';
    }

    private formatP(value: number) {
        if (value === null || value === undefined || !isFinite(value)) return '-';
        if (value < 0.001) return '< .001';
        return this.formatNumber(value, 3);
    }

    private formatPPaper(value: number) {
        if (value === null || value === undefined || !isFinite(value)) return 'p = -';
        if (value < 0.001) return 'p < .001';
        return `p = ${value.toFixed(3).replace(/^0/, '')}`;
    }

    private formatPWithPaperStars(value: number) {
        let stars = this.pStars(value);
        if (!stars || stars === 'n.s.') return this.formatPPaper(value);
        return `${this.formatPPaper(value)}, ${stars}`;
    }

    private formatStatPaper(value: number, digits: number = 2) {
        if (value === null || value === undefined || !isFinite(value)) return '-';
        let text = Number(value).toFixed(digits);
        text = text.replace(/^-0\.00$/, '0.00');
        return text.replace(/^(-?)0\./, '$1.');
    }

    private formatDfPaper(value: number) {
        if (value === null || value === undefined || !isFinite(value)) return '-';
        if (Math.abs(value - Math.round(value)) < 0.001) return String(Math.round(value));
        return this.formatStatPaper(value, 2);
    }

    private paperSignificancePhrase(p: number) {
        return p < this.modelOptions.alpha ? '통계적으로 유의하였다' : '통계적으로 유의하지 않았다';
    }

    private pStars(value: number) {
        if (value === null || value === undefined || !isFinite(value)) return '';
        if (value < 0.001) return '***';
        if (value < 0.01) return '**';
        if (value < 0.05) return '*';
        return 'n.s.';
    }

    private formatPWithStars(value: number) {
        let p = this.formatP(value);
        let stars = this.pStars(value);
        return stars ? `${p} ${stars}` : p;
    }

    private formatNumber(value: any, digits: number = 3) {
        if (value === null || value === undefined || value === '' || !isFinite(Number(value))) return '-';
        let number = Number(value);
        if (Math.abs(number) >= 100000 || (Math.abs(number) > 0 && Math.abs(number) < 0.001)) return number.toExponential(2);
        return number.toLocaleString('ko-KR', { maximumFractionDigits: digits });
    }

    private normalCdf(x: number) {
        return 0.5 * (1 + this.erf(x / Math.sqrt(2)));
    }

    private erf(x: number) {
        let sign = x >= 0 ? 1 : -1;
        x = Math.abs(x);
        let a1 = 0.254829592;
        let a2 = -0.284496736;
        let a3 = 1.421413741;
        let a4 = -1.453152027;
        let a5 = 1.061405429;
        let p = 0.3275911;
        let t = 1 / (1 + p * x);
        let y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
        return sign * y;
    }

    private tTwoTailP(t: number, df: number) {
        if (!isFinite(t) || !isFinite(df) || df <= 0) return 1;
        let x = df / (df + t * t);
        return Math.max(0, Math.min(1, this.regularizedBeta(x, df / 2, 0.5)));
    }

    private fPValue(f: number, df1: number, df2: number) {
        if (!isFinite(f) || f < 0 || df1 <= 0 || df2 <= 0) return 1;
        let x = (df1 * f) / (df1 * f + df2);
        return Math.max(0, Math.min(1, 1 - this.regularizedBeta(x, df1 / 2, df2 / 2)));
    }

    private chiSquarePValue(value: number, df: number) {
        if (!isFinite(value) || value < 0 || df <= 0) return 1;
        return Math.max(0, Math.min(1, this.regularizedGammaQ(df / 2, value / 2)));
    }

    private regularizedBeta(x: number, a: number, b: number) {
        if (x <= 0) return 0;
        if (x >= 1) return 1;
        let bt = Math.exp(this.logGamma(a + b) - this.logGamma(a) - this.logGamma(b) + a * Math.log(x) + b * Math.log(1 - x));
        if (x < (a + 1) / (a + b + 2)) return bt * this.betaContinuedFraction(x, a, b) / a;
        return 1 - bt * this.betaContinuedFraction(1 - x, b, a) / b;
    }

    private betaContinuedFraction(x: number, a: number, b: number) {
        let maxIterations = 100;
        let epsilon = 3e-7;
        let fpmin = 1e-30;
        let qab = a + b;
        let qap = a + 1;
        let qam = a - 1;
        let c = 1;
        let d = 1 - qab * x / qap;
        if (Math.abs(d) < fpmin) d = fpmin;
        d = 1 / d;
        let h = d;

        for (let m = 1; m <= maxIterations; m++) {
            let m2 = 2 * m;
            let aa = m * (b - m) * x / ((qam + m2) * (a + m2));
            d = 1 + aa * d;
            if (Math.abs(d) < fpmin) d = fpmin;
            c = 1 + aa / c;
            if (Math.abs(c) < fpmin) c = fpmin;
            d = 1 / d;
            h *= d * c;
            aa = -(a + m) * (qab + m) * x / ((a + m2) * (qap + m2));
            d = 1 + aa * d;
            if (Math.abs(d) < fpmin) d = fpmin;
            c = 1 + aa / c;
            if (Math.abs(c) < fpmin) c = fpmin;
            d = 1 / d;
            let del = d * c;
            h *= del;
            if (Math.abs(del - 1) < epsilon) break;
        }
        return h;
    }

    private regularizedGammaQ(a: number, x: number) {
        if (x < 0 || a <= 0) return 1;
        if (x === 0) return 1;
        if (x < a + 1) return 1 - this.regularizedGammaPSeries(a, x);
        return this.regularizedGammaQFraction(a, x);
    }

    private regularizedGammaPSeries(a: number, x: number) {
        let gln = this.logGamma(a);
        let ap = a;
        let sum = 1 / a;
        let del = sum;
        for (let n = 1; n <= 100; n++) {
            ap++;
            del *= x / ap;
            sum += del;
            if (Math.abs(del) < Math.abs(sum) * 1e-8) break;
        }
        return sum * Math.exp(-x + a * Math.log(x) - gln);
    }

    private regularizedGammaQFraction(a: number, x: number) {
        let gln = this.logGamma(a);
        let fpmin = 1e-30;
        let b = x + 1 - a;
        let c = 1 / fpmin;
        let d = 1 / b;
        let h = d;
        for (let i = 1; i <= 100; i++) {
            let an = -i * (i - a);
            b += 2;
            d = an * d + b;
            if (Math.abs(d) < fpmin) d = fpmin;
            c = b + an / c;
            if (Math.abs(c) < fpmin) c = fpmin;
            d = 1 / d;
            let del = d * c;
            h *= del;
            if (Math.abs(del - 1) < 1e-8) break;
        }
        return Math.exp(-x + a * Math.log(x) - gln) * h;
    }

    private logGamma(value: number) {
        let coefficients = [
            676.5203681218851,
            -1259.1392167224028,
            771.32342877765313,
            -176.61502916214059,
            12.507343278686905,
            -0.13857109526572012,
            9.9843695780195716e-6,
            1.5056327351493116e-7
        ];
        if (value < 0.5) return Math.log(Math.PI) - Math.log(Math.sin(Math.PI * value)) - this.logGamma(1 - value);
        value -= 1;
        let x = 0.99999999999980993;
        coefficients.forEach((coefficient: number, index: number) => {
            x += coefficient / (value + index + 1);
        });
        let t = value + coefficients.length - 0.5;
        return 0.5 * Math.log(2 * Math.PI) + (value + 0.5) * Math.log(t) - t + Math.log(x);
    }
}

export default PageStatisticsComponent;